import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { NavLink, useLocation } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { GiAutoRepair } from 'react-icons/gi';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/Button';
import { UserRole } from '../../lib/types/auth';
import { cn } from '../../utils/cn';
import { doc, onSnapshot, collection, query, where, getDocs, orderBy, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../lib/firebase/config';
import {
  SidebarItem,
  SidebarConfig,
  SidebarFontConfig,
  ICON_MAP,
  StoreViewEntry,
  parseStoreViewEntries,
} from '../../lib/types/sidebar';
import { isPathExcluded } from '../../lib/access/pageExclusions';
import { ProfileImage } from '../ui/ProfileImage';
import FloatingTip from '../ui/FloatingTip';
import NotificationBell from '../notifications/NotificationBell';
import { triggerSessionRefresh } from '../../services/sessionBroadcastService';
import { contentCreationService } from '../../services/contentCreationService';
import { chatNotificationService } from '../../services/chatNotificationService';
import { subscribeUnreadLiveChatCount } from '../../services/liveChatNotificationService';
import { soundNotificationService, SoundType } from '../../services/soundNotificationService';
import { SYSTEM_SOUND_DOC, DEFAULT_SYSTEM_SOUNDS, type SystemSoundConfig } from '../settings/SystemNotificationSoundsSettings';
import MicroSlats from '../effects/MicroSlats';
import { toast } from 'react-hot-toast';
import { useFloatingChat } from '../../context/FloatingChatContext';
import { useDirectChats } from '../../hooks/useDirectChats';
import { chatService } from '../../services/chat';
import { isClaireEmail } from '../../lib/claire/guide';
import { useClaireAccess, CLAIRE_BUBBLE_TOGGLE, CLAIRE_BUBBLE_CLOSE } from '../claire/ClaireBubble';
const ccoStoreCenterLogo = '/CCOSTORECENTER.png';

/** Sidebar entries that mention Claire get her animated slat background. */
const mentionsClaire = (item: SidebarItem) => /claire/i.test(item.name || '');

const ClaireBackground: React.FC = () => (
  <span aria-hidden className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
    <MicroSlats
      preset="swell"
      color="#4f100e"
      glintColor="#f6f3ae"
      backgroundColor="#1c130b"
      slatWidth={8}
      slatHeight={20}
      gap={3}
      roundness={0.75}
      interactive
      cursorStrength={1}
      cursorSize={40}
      swirl={0}
      trail={1.4}
      lean={0}
      intro
      scale={1.5}
      speed={0.6}
      direction={250}
      chop={0.55}
      stretch={0}
      glint={0.7}
      contrast={1.25}
      perspective={0.55}
      fog={0.55}
      introDuration={1.5}
      paused={false}
    />
  </span>
);

interface SidebarProps {
  isMobile?: boolean;
  isMobileOpen?: boolean;
  onClose?: () => void;
  expanded: boolean;
  setExpanded: React.Dispatch<React.SetStateAction<boolean>>;
}

const defaultSidebarItems: SidebarItem[] = [
  {
    id: 'operations',
    name: 'Operations',
    icon: 'folder',
    roles: [UserRole.MasterAdmin],
    order: 0,
    type: 'folder',
    children: [
      {
        id: 'dashboard',
        name: 'Dashboard',
        path: '/',
        icon: 'dashboard',
        roles: [
          UserRole.MasterAdmin,
          UserRole.CSAdmin,
          UserRole.AllAdmin,
          UserRole.StoreManager,
          UserRole.SalesAdmin,
          UserRole.MarketingAdmin,
        ],
        order: 0,
        type: 'link',
      },
      {
        id: 'chats',
        name: 'FIN Chats',
        path: '/chats',
        icon: 'chats',
        roles: [
          UserRole.MasterAdmin,
          UserRole.CSAdmin,
          UserRole.MarketingAdmin,
          UserRole.Marketing,
          UserRole.Template
        ],
        order: 1,
        type: 'link',
      },
      {
        id: 'pos',
        name: 'POS',
        path: '/pos',
        icon: 'pos',
        roles: [
          UserRole.MasterAdmin,
          UserRole.CSAdmin,
          UserRole.AllAdmin,
          UserRole.StoreManager,
          UserRole.SalesRep,
          UserRole.SalesAdmin,
        ],
        order: 2,
        type: 'link',
      },
      {
        id: 'pos-pin',
        name: 'POS PIN Request',
        path: '/pos-pin',
        icon: 'shield',
        roles: [UserRole.MasterAdmin, UserRole.SalesRep],
        order: 2.1,
        type: 'link',
      },
      {
        id: 'commissions',
        name: 'Commissions',
        path: '/commissions',
        icon: 'commissions',
        roles: [UserRole.SalesRep],
        order: 3,
        type: 'link',
      },
      {
        id: 'commissions-admin',
        name: 'Commissions Admin',
        path: '/commissions-admin',
        icon: 'commissions',
        roles: [
          UserRole.MasterAdmin,
          UserRole.CSAdmin,
          UserRole.CommissionAdmin
        ],
        order: 4,
        type: 'link',
      },
      {
        id: 'group-calendar',
        name: 'Group Calendar',
        path: '/group-calendar',
        icon: 'calendar',
        roles: [
          UserRole.MasterAdmin,
          UserRole.CSAdmin,
          UserRole.MarketingAdmin,
          UserRole.Marketing,
          UserRole.Template
        ],
        order: 7,
        type: 'link'
      },
      {
        id: 'content-calendar',
        name: 'Content Calendar',
        path: '/content-calendar',
        icon: 'calendar-days',
        roles: [
          UserRole.MasterAdmin,
          UserRole.CSAdmin,
          UserRole.MarketingAdmin,
          UserRole.Marketing
        ],
        order: 8,
        type: 'link'
      },
      {
        id: 'template',
        name: 'Template',
        path: '/template',
        icon: 'file',
        roles: [UserRole.Template, UserRole.MasterAdmin, UserRole.CSAdmin],
        order: 5,
        type: 'link',
      },
      {
        id: 'gem-id',
        name: 'GEM ID',
        path: '/gem-id',
        icon: 'gem',
        roles: [UserRole.MasterAdmin, UserRole.CSAdmin, UserRole.GEMID],
        order: 9,
        type: 'link',
      },
      {
        id: 'interactions',
        name: 'Customer Interactions',
        path: '/interactions',
        icon: 'users',
        roles: [UserRole.MasterAdmin, UserRole.SalesRep, UserRole.CSAdmin],
        order: 10,
        type: 'link',
      },
      {
        id: 'inquiries',
        name: 'Inquiries & Tickets',
        path: '/inquiries',
        icon: 'message-square',
        roles: [
          UserRole.MasterAdmin, 
          UserRole.CSAdmin, 
          UserRole.Marketing, 
          UserRole.Template, 
          UserRole.WatchAdmin
        ],
        order: 10.5,
        type: 'link',
      },
      {
        id: 'jewelry-requests',
        name: 'Jewelry Requests',
        path: '/jewelry-requests',
        icon: 'gem',
        roles: [UserRole.CSAdmin, UserRole.SalesRep, UserRole.Template],
        order: 11,
        type: 'link',
      },
      {
        id: 'cco-training',
        name: 'CCO Training',
        path: '/cco-training',
        icon: 'graduation-cap',
        roles: [
          UserRole.MasterAdmin,
          UserRole.CSAdmin,
          UserRole.AllAdmin,
          UserRole.StoreManager,
          UserRole.SalesAdmin,
          UserRole.SalesRep,
          UserRole.MarketingAdmin,
          UserRole.Marketing,
          UserRole.Template,
          UserRole.GEMID,
          UserRole.CommissionAdmin
        ],
        order: 12,
        type: 'link',
      },
      {
        id: 'template-training',
        name: 'Template Training',
        path: '/template-training',
        icon: 'book-open',
        roles: [UserRole.Template, UserRole.MasterAdmin],
        order: 13,
        type: 'link',
      },
      {
        id: 'template-training-admin',
        name: 'Template Training Admin',
        path: '/template-training-admin',
        icon: 'settings',
        roles: [UserRole.CSAdmin, UserRole.MasterAdmin],
        order: 14,
        type: 'link',
      },
      {
        id: 'template-admin',
        name: 'Template Admin',
        path: '/admin/templates',
        icon: 'shield-check',
        roles: [UserRole.CSAdmin, UserRole.MasterAdmin],
        order: 15,
        type: 'link',
      },
      {
        id: 'master-inventory',
        name: 'Master Inventory',
        path: '/master-inventory',
        icon: 'package',
        roles: [
          UserRole.MasterAdmin,
          UserRole.CSAdmin,
          UserRole.AllAdmin,
          UserRole.StoreManager,
          UserRole.SalesAdmin
        ],
        order: 16,
        type: 'link',
      },
      {
        id: 'shopify-orders',
        name: 'Shopify Orders',
        path: '/shopify-orders',
        icon: 'shopping-cart',
        roles: [
          UserRole.MasterAdmin,
          UserRole.CSAdmin,
          UserRole.MarketingAdmin,
          UserRole.Template
        ],
        order: 17,
        type: 'link',
      },
      {
        id: 'email-marketing',
        name: 'Email Marketing',
        path: '/email-marketing',
        icon: 'mail',
        roles: [
          UserRole.MasterAdmin,
          UserRole.Marketing,
          UserRole.MarketingAdmin
        ],
        order: 18,
        type: 'link',
        textColor: 'blue'
      },
      {
        id: 'cco-meeting-notes',
        name: 'CCO Meeting Notes',
        path: '/cco-meeting-notes',
        icon: 'file-text',
        roles: [UserRole.MasterAdmin],
        order: 19,
        type: 'link',
        textColor: 'purple'
      },
      {
        id: 'watch-warranties',
        name: 'Watch Warranties',
        path: '/watch-warranties',
        icon: 'shield',
        roles: [
          UserRole.MasterAdmin,
          UserRole.CSAdmin,
          UserRole.WatchAdmin,
          UserRole.SalesAdmin,
          UserRole.SalesRep,
          UserRole.Template,
        ],
        order: 20,
        type: 'link',
      },
      {
        id: 'repairs',
        name: 'Repairs',
        path: '/repairs',
        icon: 'GiAutoRepair',
        roles: [
          UserRole.MasterAdmin,
          UserRole.Owner,
          UserRole.AllAdmin,
          UserRole.CSAdmin,
          UserRole.MarketingAdmin,
          UserRole.StoreManager,
          UserRole.StoreStaff,
          UserRole.Tech,
          UserRole.Template,
          'Repair Admin' as UserRole,
        ],
        order: 20.5,
        type: 'link',
      },
      {
        id: 'customer-demo',
        name: 'Customer Demo',
        path: '/customer-demo',
        icon: 'users',
        roles: [
          UserRole.MasterAdmin,
          UserRole.Owner,
          UserRole.AllAdmin,
          UserRole.CSAdmin,
          UserRole.StoreManager,
          UserRole.StoreStaff,
          UserRole.SalesRep,
          UserRole.SalesAdmin,
          UserRole.Template,
        ],
        order: 20.6,
        type: 'link',
      },
      {
        id: 'company-ratings',
        name: 'Company Ratings',
        path: '/company-ratings',
        icon: 'bar-chart',
        roles: [UserRole.MasterAdmin],
        order: 21,
        type: 'link',
      },
      {
        id: 'store-till',
        name: 'Store Till',
        path: '/store-till',
        icon: 'banknote',
        roles: [
          UserRole.MasterAdmin,
          UserRole.Accounting,
          UserRole.StoreManager,
          UserRole.StoreStaff,
          UserRole.SalesRep,
          UserRole.Owner,
        ],
        order: 22,
        type: 'link',
      },
      {
        id: 'vendor-transactions',
        name: 'Vendor Transactions',
        path: '/vendor-transactions',
        icon: 'briefcase',
        roles: [
          UserRole.MasterAdmin,
          UserRole.Owner,
          UserRole.AllAdmin,
          UserRole.CSAdmin,
          UserRole.Accounting,
          UserRole.StoreManager,
        ],
        order: 23,
        type: 'link',
      }
    ],
  },
];

// Custom hook to get template review count
const useTemplateReviewCount = () => {
  const [reviewCount, setReviewCount] = useState(0);
  const { currentUser } = useAuth();

  useEffect(() => {
    if (!currentUser?.roles?.some(role => [UserRole.CSAdmin, UserRole.MasterAdmin].includes(role))) {
      return;
    }

    const fetchTemplateCount = async () => {
      try {
        // Query for templates in 'submitted' status
        const submittedQuery = query(
          collection(db, 'templates'),
          where('status', '==', 'submitted')
        );
        
        // Query for templates in 'in_review' status
        const inReviewQuery = query(
          collection(db, 'templates'),
          where('status', '==', 'in_review')
        );
        
        // Query for templates in 'final_approval' status
        const finalApprovalQuery = query(
          collection(db, 'templates'),
          where('status', '==', 'final_approval')
        );
        
        // Execute all three queries
        const [submittedSnapshot, inReviewSnapshot, finalApprovalSnapshot] = await Promise.all([
          getDocs(submittedQuery),
          getDocs(inReviewQuery),
          getDocs(finalApprovalQuery)
        ]);
        
        const totalCount = submittedSnapshot.size + inReviewSnapshot.size + finalApprovalSnapshot.size;
        setReviewCount(totalCount);
      } catch (error) {
        console.error('Error fetching template count:', error);
        setReviewCount(0);
      }
    };

    fetchTemplateCount();
    
    // Set up real-time listeners for all three statuses
    const submittedUnsubscribe = onSnapshot(
      query(collection(db, 'templates'), where('status', '==', 'submitted')),
      () => {
        fetchTemplateCount(); // Refetch total when submitted changes
      },
      (error) => {
        console.error('Error in template submitted listener:', error);
      }
    );
    
    const inReviewUnsubscribe = onSnapshot(
      query(collection(db, 'templates'), where('status', '==', 'in_review')),
      () => {
        fetchTemplateCount(); // Refetch total when in_review changes
      },
      (error) => {
        console.error('Error in template in_review listener:', error);
      }
    );
    
    const finalApprovalUnsubscribe = onSnapshot(
      query(collection(db, 'templates'), where('status', '==', 'final_approval')),
      () => {
        fetchTemplateCount(); // Refetch total when final_approval changes
      },
      (error) => {
        console.error('Error in template final_approval listener:', error);
      }
    );

    return () => {
      submittedUnsubscribe();
      inReviewUnsubscribe();
      finalApprovalUnsubscribe();
    };
  }, [currentUser]);

  return reviewCount;
};

const usePhotoLogUneditedCount = () => {
  const [uneditedCount, setUneditedCount] = useState(0);
  const { currentUser } = useAuth();

  useEffect(() => {
    if (!currentUser?.roles?.some(role => [UserRole.MasterAdmin, UserRole.CSAdmin, UserRole.Template].includes(role))) {
      return;
    }

    const fetchUneditedCount = async () => {
      try {
        // Get all months from photo_log collection
        const monthsSnapshot = await getDocs(collection(db, 'photo_log'));
        let totalUnedited = 0;

        // For each month, count unedited items in entries subcollection
        // Unedited items are those with status 'un-photod' or 'photod' (not 'edited')
        for (const monthDoc of monthsSnapshot.docs) {
          const unPhotodQuery = query(
            collection(db, 'photo_log', monthDoc.id, 'entries'),
            where('status', '==', 'un-photod')
          );
          const photodQuery = query(
            collection(db, 'photo_log', monthDoc.id, 'entries'),
            where('status', '==', 'photod')
          );
          
          const [unPhotodSnapshot, photodSnapshot] = await Promise.all([
            getDocs(unPhotodQuery),
            getDocs(photodQuery)
          ]);
          
          totalUnedited += unPhotodSnapshot.size + photodSnapshot.size;
        }

        setUneditedCount(totalUnedited);
      } catch (error) {
        console.error('Error fetching photo log unedited count:', error);
        setUneditedCount(0);
      }
    };

    fetchUneditedCount();
    
    // Set up real-time listeners for all months
    const setupListeners = async () => {
      try {
        const monthsSnapshot = await getDocs(collection(db, 'photo_log'));
        const unsubscribes: (() => void)[] = [];

        for (const monthDoc of monthsSnapshot.docs) {
          // Listen to both 'un-photod' and 'photod' status changes
          const unPhotodUnsubscribe = onSnapshot(
            query(
              collection(db, 'photo_log', monthDoc.id, 'entries'),
              where('status', '==', 'un-photod')
            ),
            () => {
              // Refetch total count when any month changes
              fetchUneditedCount();
            },
            (error) => {
              console.error(`Error in photo log un-photod listener for ${monthDoc.id}:`, error);
            }
          );
          
          const photodUnsubscribe = onSnapshot(
            query(
              collection(db, 'photo_log', monthDoc.id, 'entries'),
              where('status', '==', 'photod')
            ),
            () => {
              // Refetch total count when any month changes
              fetchUneditedCount();
            },
            (error) => {
              console.error(`Error in photo log photod listener for ${monthDoc.id}:`, error);
            }
          );
          
          unsubscribes.push(unPhotodUnsubscribe, photodUnsubscribe);
        }

        return () => {
          unsubscribes.forEach(unsub => unsub());
        };
      } catch (error) {
        console.error('Error setting up photo log listeners:', error);
        return () => {};
      }
    };

    let cleanupListeners: (() => void) | undefined;
    setupListeners().then(cleanup => {
      cleanupListeners = cleanup;
    });

    return () => {
      if (cleanupListeners) {
        cleanupListeners();
      }
    };
  }, [currentUser]);

  return uneditedCount;
};

// Custom hook to get submitted content creation count
const useContentCreationSubmittedCount = () => {
  const [submittedCount, setSubmittedCount] = useState(0);
  const { currentUser } = useAuth();

  useEffect(() => {
    if (!currentUser?.roles?.some(role => [UserRole.MasterAdmin, UserRole.Marketing, UserRole.MarketingAdmin].includes(role))) {
      return;
    }

    // Set up real-time listener for submitted content only
    const unsubscribe = contentCreationService.listenToSubmittedContentCount((count) => {
      setSubmittedCount(count);
    });

    return unsubscribe;
  }, [currentUser]);

  return submittedCount;
};

// Custom hook to get CRM follow-up count (unresolved inquiries with FLU status).
// Reads every inquiry, so it only runs for users whose sidebar shows the CRM link.
const useCrmFollowUpCount = (enabled: boolean) => {
  const [count, setCount] = useState(0);
  const { currentUser } = useAuth();

  useEffect(() => {
    if (!currentUser || !enabled) {
      setCount(0);
      return;
    }

    const q = query(collection(db, 'crm_inquiries'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      let fluCount = 0;
      snapshot.docs.forEach((d) => {
        const data = d.data();
        const notesLog = data.notesLog;
        if (!Array.isArray(notesLog) || notesLog.length === 0) return;
        const lastNote = notesLog[notesLog.length - 1]?.text?.toUpperCase() || '';
        if (/\bFLU\b/.test(lastNote)) fluCount++;
      });
      setCount(fluCount);
    }, () => setCount(0));

    return unsubscribe;
  }, [currentUser, enabled]);

  return count;
};

// Custom hook to get unread live chat count for CCO CRM combined badge
const useUnreadLiveChatSidebarCount = () => {
  const [count, setCount] = useState(0);
  const { currentUser } = useAuth();

  useEffect(() => {
    if (!currentUser) return;
    const hasValidRole = currentUser?.roles?.some(role =>
      [UserRole.MasterAdmin, UserRole.CSAdmin, UserRole.SalesAdmin, UserRole.SalesRep,
       UserRole.Template, UserRole.WatchAdmin, UserRole.Owner].includes(role)
    );
    if (!hasValidRole) { setCount(0); return; }

    const unsub = subscribeUnreadLiveChatCount((_total, unread) => setCount(unread));
    return unsub;
  }, [currentUser]);

  return count;
};

// Subscribes to the system-wide sound config doc that the Settings page writes to,
// so every browser uses whatever sound the admin picked for each event.
// Defaults to Marimba/Harp until the doc is loaded (or if it doesn't exist yet).
const useSystemSoundConfig = (): SystemSoundConfig => {
  const [cfg, setCfg] = useState<SystemSoundConfig>(DEFAULT_SYSTEM_SOUNDS);
  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, SYSTEM_SOUND_DOC),
      snap => {
        if (snap.exists()) {
          const data = snap.data() as Partial<SystemSoundConfig>;
          setCfg({
            decisionSound: (data.decisionSound as SoundType) || DEFAULT_SYSTEM_SOUNDS.decisionSound,
            entrySound: (data.entrySound as SoundType) || DEFAULT_SYSTEM_SOUNDS.entrySound,
          });
        }
      },
      err => console.error('Failed to subscribe to system sound config:', err),
    );
    return () => unsub();
  }, []);
  return cfg;
};

// Custom hook to get count of unresolved inventory decisions
// (each row in `inventoryDecisionLogs` that doesn't have `resolved: true`)
// Counts each entry; the Inventory Decisions tab itself groups by SKU+day for display.
//
// Also plays the configured "decision" sound whenever NEW decisions arrive after this
// session started (we baseline against the snapshot already on disk so existing rows
// don't trigger a flood).
const useInventoryDecisionsCount = (decisionSound: SoundType) => {
  const [count, setCount] = useState(0);
  const { currentUser } = useAuth();
  const baselineRef = React.useRef<number | null>(null);

  useEffect(() => {
    if (!currentUser) return;
    const unsubscribe = onSnapshot(
      collection(db, 'inventoryDecisionLogs'),
      (snapshot) => {
        // First snapshot establishes the baseline — anything older than the newest doc
        // we see right now counts as "already on the page" and doesn't beep.
        if (baselineRef.current === null) {
          let maxTs = 0;
          snapshot.forEach(d => {
            const t = (d.data() as { createdAt?: { toMillis?: () => number } }).createdAt?.toMillis?.() ?? 0;
            if (t > maxTs) maxTs = t;
          });
          baselineRef.current = maxTs || Date.now();
        } else {
          const additions = snapshot.docChanges().filter(c => c.type === 'added');
          let newest = baselineRef.current;
          let triggered = false;
          for (const change of additions) {
            const data = change.doc.data() as { action?: string; createdAt?: { toMillis?: () => number } };
            // Skip the bulk summary row — the per-SKU "keep" entries created with it
            // would otherwise also fire, and we'd double-trigger (or worse, fire 15x).
            if (data.action === 'keep_all') continue;
            const t = data.createdAt?.toMillis?.() ?? 0;
            if (t > baselineRef.current!) {
              triggered = true;
              if (t > newest) newest = t;
            }
          }
          if (triggered) {
            // Single sound per snapshot batch even if multiple decisions arrived together.
            soundNotificationService.playSound(decisionSound).catch(() => {});
          }
          baselineRef.current = newest;
        }

        let unresolved = 0;
        snapshot.forEach(d => {
          const data = d.data() as { resolved?: boolean; action?: string };
          // Skip the bulk "keep_all" summary row (the per-SKU "keep" rows below it already count)
          if (data.action === 'keep_all') return;
          if (data.resolved !== true) unresolved++;
        });
        setCount(unresolved);
      },
      (error) => {
        console.error('Error in inventory decisions count listener:', error);
      },
    );
    return () => unsubscribe();
  }, [currentUser, decisionSound]);

  return count;
};

// Listen for newly-submitted product entries (templates moving into 'submitted' status)
// and play the configured "entry" sound so reviewers hear the queue tick up.
// Only plays for docs that arrive AFTER this session's baseline so a fresh
// page-load doesn't beep.
const useNewTemplateSubmissionSound = (entrySound: SoundType) => {
  const { currentUser } = useAuth();
  const baselineRef = React.useRef<number | null>(null);

  useEffect(() => {
    if (!currentUser) return;
    if (!currentUser.roles?.some(role => [UserRole.CSAdmin, UserRole.MasterAdmin].includes(role))) {
      return;
    }
    const q = query(collection(db, 'templates'), where('status', '==', 'submitted'));
    const unsub = onSnapshot(
      q,
      (snapshot) => {
        if (baselineRef.current === null) {
          let maxTs = 0;
          snapshot.forEach(d => {
            const data = d.data() as { submittedAt?: { toMillis?: () => number }; createdAt?: { toMillis?: () => number }; updatedAt?: { toMillis?: () => number } };
            const t = data.submittedAt?.toMillis?.() ?? data.createdAt?.toMillis?.() ?? data.updatedAt?.toMillis?.() ?? 0;
            if (t > maxTs) maxTs = t;
          });
          baselineRef.current = maxTs || Date.now();
          return;
        }
        const additions = snapshot.docChanges().filter(c => c.type === 'added');
        let triggered = false;
        let newest = baselineRef.current;
        for (const change of additions) {
          const data = change.doc.data() as { submittedAt?: { toMillis?: () => number }; createdAt?: { toMillis?: () => number }; updatedAt?: { toMillis?: () => number } };
          const t = data.submittedAt?.toMillis?.() ?? data.createdAt?.toMillis?.() ?? data.updatedAt?.toMillis?.() ?? 0;
          if (t > baselineRef.current!) {
            triggered = true;
            if (t > newest) newest = t;
          }
        }
        if (triggered) {
          soundNotificationService.playSound(entrySound).catch(() => {});
        }
        baselineRef.current = newest;
      },
      (error) => {
        console.error('Error in new template submission sound listener:', error);
      },
    );
    return () => unsub();
  }, [currentUser, entrySound]);
};

// Custom hook to get pending jewelry requests count
const useJewelryRequestsPendingCount = () => {
  const [pendingCount, setPendingCount] = useState(0);
  const { currentUser } = useAuth();

  useEffect(() => {
    if (!currentUser?.roles?.some(role => [UserRole.MasterAdmin, UserRole.CSAdmin, UserRole.Template].includes(role))) {
      return;
    }

    // Set up real-time listener for unfulfilled jewelry requests
    const unsubscribe = onSnapshot(
      query(collection(db, 'jewelry-requests'), where('status', '==', 'pending')),
      (snapshot) => {
        setPendingCount(snapshot.size);
      },
      (error) => {
        console.error('Error in jewelry requests count listener:', error);
      }
    );

    return () => unsubscribe();
  }, [currentUser]);

  return pendingCount;
};

const useTemplateNeedsCorrectionsCount = () => {
  const [correctionsCount, setCorrectionsCount] = useState(0);
  const { currentUser } = useAuth();

  useEffect(() => {
    if (!currentUser?.roles?.some(role => [UserRole.Template, UserRole.MasterAdmin, UserRole.CSAdmin].includes(role))) {
      return;
    }

    const fetchCorrectionsCount = async () => {
      try {
        const templatesQuery = query(
          collection(db, 'templates'),
          where('status', '==', 'needs_corrections')
        );
        const querySnapshot = await getDocs(templatesQuery);
        setCorrectionsCount(querySnapshot.size);
      } catch (error) {
        console.error('Error fetching template corrections count:', error);
        setCorrectionsCount(0);
      }
    };

    fetchCorrectionsCount();
    
    // Set up real-time listener
    const unsubscribe = onSnapshot(
      query(collection(db, 'templates'), where('status', '==', 'needs_corrections')),
      (snapshot) => {
        setCorrectionsCount(snapshot.size);
      },
      (error) => {
        console.error('Error in template corrections count listener:', error);
      }
    );

    return () => unsubscribe();
  }, [currentUser]);

  return correctionsCount;
};

const useUnreadChatsCount = () => {
  const [unreadCount, setUnreadCount] = useState(0);
  const { currentUser } = useAuth();

  useEffect(() => {
    if (!currentUser?.email) {
      setUnreadCount(0);
      return;
    }

    // Check if user has required roles for FIN Chats
    const hasValidRole = currentUser?.roles?.some(role => 
      [UserRole.MasterAdmin, UserRole.CSAdmin, UserRole.MarketingAdmin, UserRole.Marketing, UserRole.Template, UserRole.WatchAdmin, UserRole.TemplateAdmin].includes(role)
    );
    
    if (!hasValidRole) {
      console.log('[Sidebar] User does not have valid role for FIN Chats, roles:', currentUser?.roles);
      setUnreadCount(0);
      return;
    }

    console.log('[Sidebar] Setting up FIN Chat unread count for:', currentUser.email);

    // Initialize chat notifications (handles sounds + desktop notifications)
    chatNotificationService.initializeChatNotifications(currentUser.email);

    let firestoreWorking = false;

    // Method 1: Direct Firestore real-time listener for unread chat notifications
    const unsubscribe = onSnapshot(
      query(
        collection(db, 'notifications'),
        where('userId', '==', currentUser.email),
        where('type', '==', 'fin_chat_message')
      ),
      (snapshot) => {
        firestoreWorking = true;
        const unread = snapshot.docs.filter(d => d.data().read === false).length;
        console.log(`[Sidebar] 🔔 Firestore unread chat notifications: ${unread} (total docs: ${snapshot.size})`);
        setUnreadCount(unread);
      },
      (error) => {
        console.error('[Sidebar] Firestore chat notifications query failed:', error);
        firestoreWorking = false;
      }
    );

    // Method 2: Fallback polling of chatNotificationService in-memory count
    // This catches cases where notification docs aren't created (e.g. duplicates, disabled)
    const pollInterval = setInterval(() => {
      const memoryCount = chatNotificationService.getTotalUnreadCount(currentUser!.email);
      if (!firestoreWorking && memoryCount > 0) {
        console.log(`[Sidebar] 🔔 Fallback poll count: ${memoryCount}`);
        setUnreadCount(memoryCount);
      } else if (firestoreWorking && memoryCount > 0) {
        // If Firestore shows 0 but memory shows > 0, use memory count
        setUnreadCount(prev => Math.max(prev, memoryCount));
      }
    }, 2000);

    return () => {
      unsubscribe();
      clearInterval(pollInterval);
    };
  }, [currentUser?.email, currentUser?.roles]);

  return unreadCount;
};

const useUnreadCalendarCount = () => {
  const [unreadCount, setUnreadCount] = useState(0);
  const { currentUser } = useAuth();

  useEffect(() => {
    if (!currentUser?.email) {
      setUnreadCount(0);
      return;
    }

    // Anyone with access to Group Calendar can see the badge
    const hasValidRole = currentUser?.roles?.some(role =>
      [UserRole.MasterAdmin, UserRole.CSAdmin, UserRole.MarketingAdmin, UserRole.Marketing, UserRole.Template].includes(role)
    );

    if (!hasValidRole) {
      setUnreadCount(0);
      return;
    }

    // Direct Firestore real-time listener for unread group calendar notifications
    const unsubscribe = onSnapshot(
      query(
        collection(db, 'notifications'),
        where('userId', '==', currentUser.email),
        where('type', '==', 'group_calendar_event')
      ),
      (snapshot) => {
        const unread = snapshot.docs.filter(d => d.data().read === false).length;
        setUnreadCount(unread);
      },
      (error) => {
        console.error('[Sidebar] Calendar notifications query failed:', error);
      }
    );

    return () => unsubscribe();
  }, [currentUser?.email, currentUser?.roles]);

  return unreadCount;
};

const useUnreadSalesCount = () => {
  const [unreadCount, setUnreadCount] = useState(0);
  const { currentUser } = useAuth();

  useEffect(() => {
    if (!currentUser?.email) {
      setUnreadCount(0);
      return;
    }

    // Anyone with access to Sales page can see the badge
    const hasValidRole = currentUser?.roles?.some(role =>
      [UserRole.MasterAdmin, UserRole.CSAdmin, UserRole.AllAdmin, UserRole.StoreManager, UserRole.SalesAdmin, UserRole.SalesRep].includes(role)
    );

    if (!hasValidRole) {
      setUnreadCount(0);
      return;
    }

    // Direct Firestore real-time listener for unread sales notifications
    const unsubscribe = onSnapshot(
      query(
        collection(db, 'notifications'),
        where('userId', '==', currentUser.email),
        where('type', '==', 'cco_sale_added')
      ),
      (snapshot) => {
        const unread = snapshot.docs.filter(d => d.data().read === false).length;
        setUnreadCount(unread);
      },
      (error) => {
        console.error('[Sidebar] Sales notifications query failed:', error);
      }
    );

    return () => unsubscribe();
  }, [currentUser?.email, currentUser?.roles]);

  return unreadCount;
};

const useNewSurveyResponseCount = () => {
  const [count, setCount] = useState(0);
  const { currentUser } = useAuth();
  const [lastViewed, setLastViewed] = useState<Timestamp>(() => {
    const stored = localStorage.getItem('surveyResponsesLastViewed');
    if (stored) return Timestamp.fromMillis(parseInt(stored));
    localStorage.setItem('surveyResponsesLastViewed', Date.now().toString());
    return Timestamp.now();
  });

  useEffect(() => {
    const handler = () => {
      const stored = localStorage.getItem('surveyResponsesLastViewed');
      setLastViewed(stored ? Timestamp.fromMillis(parseInt(stored)) : Timestamp.fromMillis(0));
    };
    window.addEventListener('surveyResponsesViewed', handler);
    return () => window.removeEventListener('surveyResponsesViewed', handler);
  }, []);

  useEffect(() => {
    if (!currentUser?.roles?.some(role => [UserRole.MasterAdmin].includes(role))) return;

    let responseUnsubs: (() => void)[] = [];

    const unsubSurveys = onSnapshot(
      collection(db, 'customer_surveys'),
      (surveysSnap) => {
        responseUnsubs.forEach(u => u());
        responseUnsubs = [];

        const surveyIds = surveysSnap.docs.map(d => d.id);
        if (surveyIds.length === 0) { setCount(0); return; }

        const perSurveyCount: Record<string, number> = {};

        for (const surveyId of surveyIds) {
          const unsub = onSnapshot(
            query(
              collection(db, 'customer_surveys', surveyId, 'responses'),
              where('submittedAt', '>', lastViewed)
            ),
            (respSnap) => {
              perSurveyCount[surveyId] = respSnap.size;
              setCount(Object.values(perSurveyCount).reduce((a, b) => a + b, 0));
            }
          );
          responseUnsubs.push(unsub);
        }
      }
    );

    return () => {
      unsubSurveys();
      responseUnsubs.forEach(u => u());
    };
  }, [currentUser, lastViewed]);

  return count;
};

const useUnreadStoreEmailCount = () => {
  const [count, setCount] = useState(0);
  const { currentUser } = useAuth();

  useEffect(() => {
    if (!currentUser?.roles) return;
    const hasRole = currentUser.roles.some(r =>
      [UserRole.MasterAdmin, UserRole.Owner, UserRole.AllAdmin, UserRole.StoreManager, UserRole.StoreStaff, UserRole.CSAdmin, UserRole.SalesAdmin, UserRole.SalesRep].includes(r)
    );
    if (!hasRole) { setCount(0); return; }

    const unsub = onSnapshot(
      doc(db, 'settings', 'store_email_meta'),
      (snap) => {
        setCount(snap.data()?.unreadCount || 0);
      },
      () => setCount(0),
    );
    return unsub;
  }, [currentUser?.roles]);

  return count;
};

/**
 * True when the current user has been granted view access to at least one
 * customer survey or internal rating cycle's results (via resultsAllowedRoles).
 * Master Admins always have access and short-circuit to true. Used to reveal
 * the Company Ratings sidebar link to non-admin viewers.
 */
const useHasSharedSurveyResults = (): boolean => {
  const { currentUser } = useAuth();
  const [hasSurvey, setHasSurvey] = useState(false);
  const [hasCycle, setHasCycle] = useState(false);

  const roles = currentUser?.roles;
  const isMasterAdmin = !!roles?.includes(UserRole.MasterAdmin);

  useEffect(() => {
    if (!currentUser || isMasterAdmin || !roles || roles.length === 0) {
      setHasSurvey(false);
      setHasCycle(false);
      return;
    }
    const intersects = (allowed: any): boolean =>
      Array.isArray(allowed) && allowed.some((r: string) => roles.includes(r as UserRole));

    const unsubSurveys = onSnapshot(collection(db, 'customer_surveys'), snap => {
      setHasSurvey(snap.docs.some(d => intersects(d.data().resultsAllowedRoles)));
    }, () => setHasSurvey(false));

    const unsubCycles = onSnapshot(collection(db, 'company_ratings'), snap => {
      setHasCycle(snap.docs.some(d => intersects(d.data().resultsAllowedRoles)));
    }, () => setHasCycle(false));

    return () => { unsubSurveys(); unsubCycles(); };
  }, [currentUser, isMasterAdmin, JSON.stringify(roles)]);

  return isMasterAdmin || hasSurvey || hasCycle;
};

/**
 * Converts a router path (`/photo-log`, `/cco-crm`, `/`) into a friendly
 * label for the sidebar's active-users hover popup. Hyphens become spaces,
 * each word is title-cased, and a few high-signal abbreviations are
 * preserved in their canonical casing (e.g. CRM, POS, AI, GEM, CCO, FIN).
 */
const ACTIVE_PAGE_LABEL_OVERRIDES: Record<string, string> = {
  '': 'Dashboard',
  '/': 'Dashboard',
};

const ACTIVE_PAGE_ALL_CAPS_TOKENS = new Set([
  'crm', 'pos', 'ai', 'gem', 'cco', 'fin', 'cs', 'sms', 'csv', 'pdf', 'sku',
  'obs', 'id', 'ccoc', 'gemid', 'sfs', 'im', 'cco-crm',
]);

const formatActivePagePath = (path: string | undefined | null): string => {
  if (!path) return 'Idle';
  const normalized = path.split('?')[0].split('#')[0].replace(/\/+$/, '');
  if (normalized in ACTIVE_PAGE_LABEL_OVERRIDES) {
    return ACTIVE_PAGE_LABEL_OVERRIDES[normalized];
  }
  // Use the FIRST path segment as the page; deeper segments are usually
  // ids/sub-tabs that aren't useful in a hover tooltip.
  const segment = normalized.replace(/^\//, '').split('/')[0];
  if (!segment) return 'Dashboard';
  return segment
    .split('-')
    .map((word) =>
      ACTIVE_PAGE_ALL_CAPS_TOKENS.has(word.toLowerCase())
        ? word.toUpperCase()
        : word.charAt(0).toUpperCase() + word.slice(1),
    )
    .join(' ');
};


export const Sidebar: React.FC<SidebarProps> = ({
  isMobile = false,
  isMobileOpen = true,
  onClose,
  expanded,
  setExpanded,
}) => {
  const [openFolders, setOpenFolders] = useState<Record<string, boolean>>({});
  const [sidebarConfig, setSidebarConfig] = useState<SidebarItem[]>(defaultSidebarItems);
  // Store View sequence (category headers + link ids). Configured in Settings
  // and stored in Firestore at settings/store_view_config.
  const [storeViewEntries, setStoreViewEntries] = useState<StoreViewEntry[]>([]);
  // "Store View" preview: lets Master / Marketing admins see the sidebar
  // exactly as a Store Manager would (flattened + role-restricted). Persisted
  // in localStorage so it survives navigation/reloads until toggled off.
  const [storeViewActive, setStoreViewActive] = useState<boolean>(() => {
    try {
      return localStorage.getItem('sidebar_store_view') === '1';
    } catch {
      return false;
    }
  });
  // Search query for the in-sidebar finder. Empty string = show full tree.
  const [sidebarSearchQuery, setSidebarSearchQuery] = useState('');
  const [sidebarFontConfig, setSidebarFontConfig] = useState<SidebarFontConfig>({
    fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
    fontSize: 14,
    fontWeight: '500',
    fontStyle: 'normal',
    lineHeight: 1.4,
    letterSpacing: 0
  });
  const [activeUsers, setActiveUsers] = useState<any[]>([]);
  const activeUsersSignatureRef = React.useRef('');
  const [userTip, setUserTip] = useState<{ id: string; el: HTMLElement } | null>(null);
  const { currentUser, logout } = useAuth();
  const location = useLocation();
  const hasSharedSurveyResults = useHasSharedSurveyResults();
  const templateReviewCount = useTemplateReviewCount();
  const photoLogUneditedCount = usePhotoLogUneditedCount();
  const templateNeedsCorrectionsCount = useTemplateNeedsCorrectionsCount();
  const unreadChatsCount = useUnreadChatsCount();
  const unreadCalendarCount = useUnreadCalendarCount();
  const unreadSalesCount = useUnreadSalesCount();
  const contentCreationSubmittedCount = useContentCreationSubmittedCount();
  const jewelryRequestsPendingCount = useJewelryRequestsPendingCount();
  const systemSounds = useSystemSoundConfig();
  const inventoryDecisionsCount = useInventoryDecisionsCount(systemSounds.decisionSound);
  useNewTemplateSubmissionSound(systemSounds.entrySound);
  const unreadLiveChatSidebarCount = useUnreadLiveChatSidebarCount();
  const newSurveyResponseCount = useNewSurveyResponseCount();
  const unreadStoreEmailCount = useUnreadStoreEmailCount();
  const isMasterAdmin = currentUser?.roles?.includes(UserRole.MasterAdmin) || false;
  const effectiveExpanded = true;
  const [userDirectory, setUserDirectory] = useState<Map<string, any>>(new Map());
  const { floatingChat, activateFloatingChat, deactivateFloatingChat } = useFloatingChat();
  const hasClaireAccess = useClaireAccess();
  const { directChats, unreadFrom, markSeen } = useDirectChats(currentUser?.email, currentUser?.uid);
  const myEmail = (currentUser?.email || '').toLowerCase();

  // Anything open on screen counts as seen, including new messages that arrive while it's open.
  useEffect(() => {
    const open = Object.values(unreadFrom).find(
      (c) =>
        (floatingChat.isActive && !floatingChat.isCollapsed && floatingChat.chatId === c.chatId) ||
        location.pathname === `/chats/${c.chatId}`
    );
    if (open) markSeen(open.chatId);
  }, [unreadFrom, floatingChat, location.pathname, markSeen]);

  // Claire first (always there for people who can use her), then anyone with an unread message, then everyone else active.
  const stripUsers = useMemo(() => {
    const byEmail = new Map<string, any>();
    activeUsers.forEach((u) => byEmail.set(String(u.email).toLowerCase(), u));
    const claireUser = Array.from(userDirectory.values()).find((u) => isClaireEmail(u.email));
    const claireEmail = claireUser ? String(claireUser.email).toLowerCase() : '';
    if (hasClaireAccess && claireUser && !byEmail.has(claireEmail)) {
      byEmail.set(claireEmail, { ...claireUser, offline: true });
    }
    Object.keys(unreadFrom).forEach((email) => {
      if (byEmail.has(email)) return;
      const known = userDirectory.get(email);
      byEmail.set(email, known
        ? { ...known, offline: true }
        : { userId: email, email, displayName: email.split('@')[0], photoURL: null, offline: true });
    });
    const rank = (u: any) => {
      const email = String(u.email).toLowerCase();
      if (email === claireEmail) return 0;
      if (unreadFrom[email]) return 1;
      return 2;
    };
    return Array.from(byEmail.values()).sort((a, b) => rank(a) - rank(b));
  }, [activeUsers, userDirectory, unreadFrom, hasClaireAccess]);

  const openUserChat = async (user: any) => {
    const email = String(user.email || '').toLowerCase();
    if (!currentUser?.email || !email || email === myEmail) return;
    const unread = unreadFrom[email];
    if (isClaireEmail(email) && hasClaireAccess && !unread) {
      deactivateFloatingChat();
      window.dispatchEvent(new Event(CLAIRE_BUBBLE_TOGGLE));
      return;
    }
    try {
      const chatId = directChats[email]?.chatId
        || await chatService.findOrCreateDirectChat(currentUser.email, currentUser.uid, email);
      window.dispatchEvent(new Event(CLAIRE_BUBBLE_CLOSE));
      activateFloatingChat(chatId, { displayName: user.displayName });
      markSeen(chatId);
      chatNotificationService.markChatAsRead(currentUser.email, chatId);
    } catch (error) {
      console.error('Error opening FIN chat:', error);
      toast.error(`Couldn't open a FIN chat with ${user.displayName}`);
    }
  };

  // Debug: log unread chats count on every render
  useEffect(() => {
    console.log(`[Sidebar] 🔔 unreadChatsCount = ${unreadChatsCount}, unreadCalendarCount = ${unreadCalendarCount}, unreadSalesCount = ${unreadSalesCount}, user = ${currentUser?.email}`);
  }, [unreadChatsCount, unreadCalendarCount, currentUser?.email]);

  // Clear chat notifications when user is on the FIN Chats page
  useEffect(() => {
    if (!currentUser?.email || !location.pathname.startsWith('/chats')) return;

    const clearChatNotifications = async () => {
      try {
        // Mark all unread FIN Chat notifications as read in Firestore
        const unreadQuery = query(
          collection(db, 'notifications'),
          where('userId', '==', currentUser.email),
          where('type', '==', 'fin_chat_message')
        );
        const snapshot = await getDocs(unreadQuery);
        const updates = snapshot.docs
          .filter(d => d.data().read === false)
          .map(d => updateDoc(d.ref, { read: true, updatedAt: new Date().toISOString() }));
        
        if (updates.length > 0) {
          await Promise.all(updates);
          console.log(`[Sidebar] Marked ${updates.length} chat notifications as read`);
        }

        // Also clear the in-memory count
        chatNotificationService.markAllChatsAsRead(currentUser.email);
      } catch (error) {
        console.error('[Sidebar] Error clearing chat notifications:', error);
      }
    };

    clearChatNotifications();
  }, [location.pathname, currentUser?.email]);

  // Clear calendar notifications when user NAVIGATES TO the Group Calendar page
  // Uses a ref to track the timestamp of navigation so we only clear pre-existing notifications
  const calendarNavTimeRef = React.useRef<number>(0);
  useEffect(() => {
    if (!currentUser?.email || !location.pathname.startsWith('/group-calendar')) {
      calendarNavTimeRef.current = 0;
      return;
    }

    // Record the time the user navigated to the page
    const navTime = Date.now();
    calendarNavTimeRef.current = navTime;

    const clearCalendarNotifications = async () => {
      try {
        const unreadQuery = query(
          collection(db, 'notifications'),
          where('userId', '==', currentUser.email),
          where('type', '==', 'group_calendar_event')
        );
        const snapshot = await getDocs(unreadQuery);
        // Only clear notifications that existed BEFORE the user navigated to the page
        const updates = snapshot.docs
          .filter(d => {
            const data = d.data();
            if (data.read !== false) return false;
            // Don't clear notifications created after the user navigated to the page
            const createdAt = data.createdAt ? new Date(data.createdAt).getTime() : 0;
            return createdAt < navTime;
          })
          .map(d => updateDoc(d.ref, { read: true, updatedAt: new Date().toISOString() }));

        if (updates.length > 0) {
          await Promise.all(updates);
          console.log(`[Sidebar] Marked ${updates.length} pre-existing calendar notifications as read`);
        }
      } catch (error) {
        console.error('[Sidebar] Error clearing calendar notifications:', error);
      }
    };

    clearCalendarNotifications();
  }, [location.pathname, currentUser?.email]);

  // Clear sales notifications when user navigates to the Sales page
  useEffect(() => {
    if (!currentUser?.email || !location.pathname.startsWith('/sales')) return;

    const clearSalesNotifications = async () => {
      try {
        const unreadQuery = query(
          collection(db, 'notifications'),
          where('userId', '==', currentUser.email),
          where('type', '==', 'cco_sale_added')
        );
        const snapshot = await getDocs(unreadQuery);
        const updates = snapshot.docs
          .filter(d => d.data().read === false)
          .map(d => updateDoc(d.ref, { read: true, updatedAt: new Date().toISOString() }));

        if (updates.length > 0) {
          await Promise.all(updates);
          console.log(`[Sidebar] Marked ${updates.length} sales notifications as read`);
        }
      } catch (error) {
        console.error('[Sidebar] Error clearing sales notifications:', error);
      }
    };

    clearSalesNotifications();
  }, [location.pathname, currentUser?.email]);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, 'settings', 'sidebar_config'),
      (doc) => {
        if (doc.exists()) {
          const data = doc.data() as SidebarConfig;
          console.log('Loaded sidebar config:', data);
          if (data.items && Array.isArray(data.items)) {
            // Filter out hidden items
            const filterHiddenItems = (items: SidebarItem[]): SidebarItem[] => {
              return items
                .filter(item => !item.hidden)
                .map(item => {
                  if (item.type === 'folder' && item.children) {
                    return {
                      ...item,
                      children: filterHiddenItems(item.children)
                    };
                  }
                  return item;
                });
            };
            
            const visibleItems = filterHiddenItems(data.items);
            setSidebarConfig(visibleItems);
            console.log('Current user roles:', currentUser?.roles);
            console.log('Sidebar items (visible only):', visibleItems);
          } else {
            console.warn('Invalid sidebar config, using default:', defaultSidebarItems);
            setSidebarConfig(defaultSidebarItems);
          }
          
          // Load font configuration if it exists
          if (data.fontConfig) {
            setSidebarFontConfig(data.fontConfig);
          }
        } else {
          console.warn('No sidebar config found, using default:', defaultSidebarItems);
          setSidebarConfig(defaultSidebarItems);
        }
      },
      (error) => {
        console.error('Firestore error:', error);
        setSidebarConfig(defaultSidebarItems);
      }
    );
    return () => unsubscribe();
  }, []);

  // Custom Store Staff / Manager menu order + category headers.
  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, 'settings', 'store_view_config'),
      (snap) => {
        setStoreViewEntries(parseStoreViewEntries(snap.data()));
      },
      () => setStoreViewEntries([])
    );
    return () => unsub();
  }, []);

  // Fetch active users
  useEffect(() => {
    const fetchActiveUsers = async () => {
      try {
        // Get all users from the users collection
        const usersQuery = query(collection(db, 'users'));
        const usersSnapshot = await getDocs(usersQuery);
        
        // Create a map of all users with their basic info
        const allUsersMap = new Map();
        usersSnapshot.docs.forEach(doc => {
          const userData = doc.data();
          
          // Filter out deleted users
          if (userData.status === 'deleted') {
            return; // Skip deleted users
          }
          
          allUsersMap.set(doc.id, {
            userId: doc.id,
            displayName: userData.displayName || userData.email?.split('@')[0] || 'Unknown User',
            email: userData.email || 'unknown@example.com',
            photoURL: userData.photoURL || null,
            roles: userData.roles || [],
            // Default activity values
            currentPage: '',
            userAgent: '',
            lastActive: new Date(0), // Very old date for users with no activity
            isActive: false
          });
        });

        if (allUsersMap.size === 0) {
          setActiveUsers([]);
          return;
        }
        setUserDirectory(new Map(Array.from(allUsersMap.values()).map((u: any) => [String(u.email).toLowerCase(), u])));

        // Listen to activity data and merge with user data
        const activityQuery = query(
          collection(db, 'userActivity'),
          orderBy('lastActive', 'desc')
        );
        
        const unsubscribe = onSnapshot(activityQuery, 
          (snapshot) => {
            // Update activity data for users who have it
            snapshot.docs.forEach(doc => {
              const data = doc.data();
              const lastActive = data.lastActive?.toDate() || new Date();
              const timeDiff = Date.now() - lastActive.getTime();
              // Claire checks in every 5 minutes while on shift, so she gets a wider window than people do.
              const isActive = timeDiff < (data.userAgent === 'Claire' ? 7 : 3) * 60 * 1000;
              
              if (allUsersMap.has(data.userId)) {
                const userData = allUsersMap.get(data.userId);
                allUsersMap.set(data.userId, {
                  ...userData,
                  currentPage: data.currentPage || '',
                  activity: data.activity || '',
                  userAgent: data.userAgent || '',
                  lastActive,
                  isActive
                });
              }
            });

            // Convert map to array and filter for only active users
            const allUsers = Array.from(allUsersMap.values());
            const onlyActiveUsers = allUsers.filter(user => user.isActive);

            // Every open tab writes a heartbeat; only re-render when who is online (or where) changes.
            const signature = onlyActiveUsers
              .map((u: any) => `${u.userId}|${u.currentPage}|${u.activity}|${u.userAgent}`)
              .sort()
              .join('\n');
            if (signature === activeUsersSignatureRef.current) return;
            activeUsersSignatureRef.current = signature;
            setActiveUsers(onlyActiveUsers);
          },
          (error) => {
            console.error('Error listening to user activity:', error);
            setActiveUsers([]);
          }
        );

        return unsubscribe;
      } catch (error) {
        console.error('Error fetching active users:', error);
        setActiveUsers([]);
      }
    };

    fetchActiveUsers();
  }, []);

  useEffect(() => {
    if (isMobile && isMobileOpen) {
      setExpanded(true);
    }
  }, [isMobile, isMobileOpen]);

  const toggleFolder = (name: string) => {
    setOpenFolders((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const userInfo = {
    uid: currentUser?.uid,
    name: currentUser?.displayName || 'User',
    email: currentUser?.email || '',
    role: currentUser?.roles?.[0] || UserRole.StoreStaff,
    photoURL: currentUser?.photoURL || null,
  };

  // Roles that have access to ALL pages regardless of individual page settings
  const FULL_ACCESS_ROLES: UserRole[] = [
    UserRole.MasterAdmin,
    UserRole.Owner,
    UserRole.AllAdmin,
  ];

  // Only Master / Marketing admins may preview the Store Manager sidebar —
  // plus a small per-user allowlist for specific staff who need it. Allowlisted
  // staff preview the Store STAFF view (matching their own store role), while
  // admins preview the Store Manager view.
  const STORE_VIEW_PREVIEW_EMAILS = ['acarmona@chorostandco.com'];
  const isStoreViewAllowlisted =
    !!currentUser?.email && STORE_VIEW_PREVIEW_EMAILS.includes(currentUser.email.toLowerCase());
  const isStoreViewAdmin =
    currentUser?.roles?.some(
      (r) => r === UserRole.MasterAdmin || r === UserRole.MarketingAdmin
    ) ?? false;
  const canPreviewStoreView = isStoreViewAdmin || isStoreViewAllowlisted;
  // Which role the preview emulates: admins see the Store Manager menu;
  // allowlisted staff see the Store Staff menu.
  const storeViewPreviewRole: UserRole =
    !isStoreViewAdmin && isStoreViewAllowlisted ? UserRole.StoreStaff : UserRole.StoreManager;
  // While previewing, evaluate the ENTIRE sidebar as if the user held only that
  // store role, so access + layout match what store staff actually see.
  const storeViewPreview = storeViewActive && canPreviewStoreView;
  const effectiveRoles: UserRole[] = storeViewPreview
    ? [storeViewPreviewRole]
    : (currentUser?.roles ?? []);

  const hasAccess = (roles: UserRole[]): boolean => {
    if (!Array.isArray(effectiveRoles) || !Array.isArray(roles))
      return false;
    
    // Full access roles can see everything
    if (effectiveRoles.some(role => FULL_ACCESS_ROLES.includes(role))) {
      return true;
    }
    
    return roles.length === 0 || effectiveRoles.some((role) => roles.includes(role));
  };

  const isActive = (path?: string): boolean => {
    if (!path) return false;
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const getIconColorClass = (color?: string, customColor?: string) => {
    if (color === 'custom' && customColor) return '';
    switch (color) {
      // Original colors
      case 'red': return 'text-red-400';
      case 'yellow': return 'text-yellow-400';
      case 'orange': return 'text-orange-400';
      case 'green': return 'text-emerald-400';
      case 'blue': return 'text-blue-400';
      case 'purple': return 'text-purple-400';
      case 'pink': return 'text-pink-400';
      case 'teal': return 'text-teal-400';
      case 'indigo': return 'text-indigo-400';
      case 'rose': return 'text-rose-400';
      case 'slate': return 'text-slate-400';
      case 'cyan': return 'text-cyan-400';
      case 'lime': return 'text-lime-400';
      case 'amber': return 'text-amber-400';
      case 'emerald': return 'text-emerald-400';
      case 'violet': return 'text-violet-400';
      case 'fuchsia': return 'text-fuchsia-400';
      case 'sky': return 'text-sky-400';
      case 'gray': return 'text-gray-400';
      case 'stone': return 'text-stone-400';
      case 'zinc': return 'text-zinc-400';
      case 'neutral': return 'text-neutral-400';
      case 'black': return 'text-gray-800';
      case 'crimson': return 'text-red-600';
      case 'gold': return 'text-yellow-500';
      case 'silver': return 'text-slate-300';
      case 'bronze': return 'text-amber-600';
      case 'mint': return 'text-green-300';
      case 'coral': return 'text-orange-300';
      
      // NEW Solid Colors - Gemstone & Metal Theme
      case 'ruby': return 'text-red-500';
      case 'sapphire': return 'text-blue-600';
      case 'topaz': return 'text-yellow-600';
      case 'onyx': return 'text-gray-900';
      case 'pearl': return 'text-gray-100';
      case 'platinum': return 'text-gray-200';
      case 'copper': return 'text-orange-600';
      case 'steel': return 'text-gray-500';
      case 'iron': return 'text-gray-600';
      case 'titanium': return 'text-gray-300';
      
      // Special Effect Colors
      case 'neon': return 'text-green-400 drop-shadow-[0_0_8px_rgba(34,197,94,0.8)]';
      case 'electric': return 'text-blue-400 drop-shadow-[0_0_8px_rgba(59,130,246,0.8)]';
      case 'glow': return 'text-yellow-300 drop-shadow-[0_0_8px_rgba(253,224,71,0.8)]';
      case 'flame': return 'text-red-500 drop-shadow-[0_0_8px_rgba(239,68,68,0.8)]';
      case 'ice': return 'text-cyan-300 drop-shadow-[0_0_8px_rgba(103,232,249,0.8)]';
      case 'storm': return 'text-indigo-400 drop-shadow-[0_0_8px_rgba(129,140,248,0.8)]';
      case 'sunset': return 'text-orange-400';
      case 'dawn': return 'text-pink-300';
      case 'midnight': return 'text-indigo-900';
      case 'rainbow': return 'text-purple-400'; // Solid color for icons (gradient is in text)
      case 'aurora': return 'text-blue-500'; // Solid color for icons (gradient is in text)
      
      // GRADIENT Colors - Use solid color equivalents for icons (gradients don't work on SVG icons)
      // The text labels will still show gradients via getTextColorClass
      case 'gradient-fire': return 'text-orange-500';
      case 'gradient-ocean': return 'text-cyan-500';
      case 'gradient-sunset': return 'text-pink-500';
      case 'gradient-aurora': return 'text-purple-500';
      case 'gradient-cosmic': return 'text-purple-700';
      case 'gradient-forest': return 'text-emerald-600';
      case 'gradient-royal': return 'text-blue-700';
      case 'gradient-neon': return 'text-green-400 drop-shadow-[0_0_8px_rgba(34,197,94,0.6)]';
      case 'gradient-ice': return 'text-cyan-300';
      case 'gradient-volcano': return 'text-orange-600';
      case 'gradient-galaxy': return 'text-purple-600';
      case 'gradient-rainbow': return 'text-purple-500';
      
      default: return 'text-gray-300';
    }
  };

  const renderIcon = (iconName?: string, color?: string, customColor?: string) => {
    if (!iconName) return null;
    
    const iconColorClass = getIconColorClass(color, customColor);
    const customStyle = color === 'custom' && customColor ? { color: customColor } : undefined;
    
    // Handle React Icons (GiAutoRepair)
    if (iconName === 'GiAutoRepair' || iconName === 'autorepair') {
      return <GiAutoRepair size={20} className={iconColorClass} style={customStyle} />;
    }
    
    // First try direct Lucide icon name (new system)
    let IconComponent = (Icons as any)[iconName];
    
    // If not found, try the legacy ICON_MAP system
    if (!IconComponent) {
      const mappedName = ICON_MAP[iconName.toLowerCase()];
      IconComponent = mappedName ? (Icons as any)[mappedName] : null;
    }
    
    // Final fallback to Circle
    if (!IconComponent) {
      IconComponent = Icons.Circle;
    }
    //*sidebar icon size and stroke width
  
    return <IconComponent size={20} strokeWidth={1.25} className={iconColorClass} style={customStyle} />;
  };

  // Helper to match the FIN Chats item by id, path, or name (Firestore config may use different ids)
  const isChatItem = (item: SidebarItem) => 
    item.id === 'chats' || item.path === '/chats' || item.name?.toLowerCase().includes('fin chat');

  // Helper to match the Group Calendar item
  const isCalendarItem = (item: SidebarItem) => 
    item.id === 'group-calendar' || item.path === '/group-calendar' || item.name?.toLowerCase().includes('group calendar');

  // Helper to match the Sales / CCO Sales item
  const isSalesItem = (item: SidebarItem) => 
    item.id === 'sales' || item.id === 'cco-sales' || item.path === '/sales' || item.name?.toLowerCase().includes('sales');

  // Helper to match the CCO CRM item
  const isCrmItem = (item: SidebarItem) =>
    item.id === 'cco-crm' || item.path === '/cco-crm' || item.name?.toLowerCase().includes('cco crm');

  // Helper to match the Repairs item (the FIN repair log, not CCO Repairs)
  const isRepairsItem = (item: SidebarItem) =>
    item.id === 'repairs' || item.path === '/repairs';

  // Helper to match the Company Ratings item
  const isCompanyRatingsItem = (item: SidebarItem) =>
    item.id === 'company-ratings' || item.path === '/company-ratings' || item.name?.toLowerCase().includes('company ratings');

  // Helper to match the Store Email item
  const isStoreEmailItem = (item: SidebarItem) =>
    item.id === 'store-email' || item.path === '/store-email' || (item.name || '').toLowerCase().includes('store email');

  // Helper to match the Inventory Database item (the Decisions tab lives inside this page)
  const isInventoryDatabaseItem = (item: SidebarItem) =>
    item.id === 'inventory-database' ||
    item.path === '/inventory-database' ||
    item.name?.toLowerCase().includes('inventory database');

  // Access to a sidebar item by role, PLUS the store-calendar exception:
  // users granted storeCalendarAccess can always see the Group Calendar link
  // (they land on it and switch to the Store calendar via the top dropdown).
  // Special (non-role) access grants for specific links:
  // - Group Calendar: store-calendar access or Store Manager/Staff role
  // - Company Ratings: results shared with the user's role
  const hasSpecialAccess = (item: SidebarItem): boolean => {
    if (isCalendarItem(item)) {
      const storeRole = currentUser?.roles?.some(
        (r) => r === UserRole.StoreManager || r === UserRole.StoreStaff
      );
      if (!!currentUser?.storeCalendarAccess || storeRole) return true;
    }
    if (isCompanyRatingsItem(item) && hasSharedSurveyResults) return true;
    // Jewelers hold no role, but they need Repairs to submit their quotes.
    if (isRepairsItem(item) && !!currentUser?.jewelerAccess) return true;
    return false;
  };

  const hasItemAccess = (item: SidebarItem): boolean => {
    if (item.type === 'folder') {
      if (hasAccess(item.roles)) return true;
      // Reveal an otherwise-hidden folder when it holds a child the user has
      // special (non-role) access to, so that child link is reachable.
      return (item.children || []).some((c) => hasSpecialAccess(c));
    }
    if (hasAccess(item.roles)) return true;
    return hasSpecialAccess(item);
  };

  // Map a single sidebar item to its current notification count, mirroring
  // every per-item badge rule used below. Returns 0 when the item has no
  // associated counter (or the counter is zero / item is hidden by access).
  const itemNotificationCount = (item: SidebarItem): number => {
    if (!hasItemAccess(item)) return 0;
    if (item.id === 'template-admin') return templateReviewCount || 0;
    if (item.id === 'template') return templateNeedsCorrectionsCount || 0;
    if (isChatItem(item)) return unreadChatsCount || 0;
    if (isCalendarItem(item)) return unreadCalendarCount || 0;
    if (isSalesItem(item)) return unreadSalesCount || 0;
    if (item.id === 'photo-log') return photoLogUneditedCount || 0;
    if (item.id === 'content-creation') return contentCreationSubmittedCount || 0;
    if (item.id === 'jewelry-requests') return jewelryRequestsPendingCount || 0;
    if (isCrmItem(item)) return (crmFollowUpCount || 0) + (unreadLiveChatSidebarCount || 0);
    if (isCompanyRatingsItem(item)) return newSurveyResponseCount || 0;
    if (isInventoryDatabaseItem(item)) return inventoryDecisionsCount || 0;
    if (isStoreEmailItem(item)) return unreadStoreEmailCount || 0;
    return 0;
  };

  // Sum every descendant's notification count so a collapsed folder can
  // surface "there's something new inside" without the user having to open
  // it. Recurses into nested folders so deeply-nested unreads still bubble
  // all the way to the top-level folder.
  const folderTotalNotifications = (folder: SidebarItem): number => {
    if (folder.type !== 'folder') return 0;
    let total = 0;
    for (const child of folder.children || []) {
      if (child.type === 'folder') {
        total += folderTotalNotifications(child);
      } else {
        total += itemNotificationCount(child);
      }
    }
    return total;
  };

  // Per-source badge style. We keep these as separate pills on collapsed folders
  // so e.g. Photo Log unread + Inventory Decisions don't get summed into a single
  // ambiguous number (that'd lose what the badge actually represents).
  type SidebarBadge = { key: string; label: string; count: number; classes: string };

  // Return the badges this single item contributes (an array because some items
  // contribute multiple — e.g. CCO CRM has follow-ups + live-chat unreads).
  const itemBadges = (item: SidebarItem): SidebarBadge[] => {
    if (!hasItemAccess(item)) return [];
    const out: SidebarBadge[] = [];
    if (item.id === 'template-admin' && templateReviewCount > 0) {
      out.push({ key: 'template-admin', label: 'Template Admin reviews', count: templateReviewCount, classes: 'bg-[#f1e8d2] border-[#d8c59a] text-[#5a3a1a]' });
    }
    if (item.id === 'template' && templateNeedsCorrectionsCount > 0) {
      out.push({ key: 'template', label: 'Templates needing corrections', count: templateNeedsCorrectionsCount, classes: 'bg-[#f1e8d2] border-[#d8c59a] text-[#5a3a1a]' });
    }
    if (isChatItem(item) && unreadChatsCount > 0) {
      out.push({ key: 'chats', label: 'Unread FIN chats', count: unreadChatsCount, classes: 'bg-[#f1e8d2] border-[#d8c59a] text-[#5a3a1a]' });
    }
    if (isCalendarItem(item) && unreadCalendarCount > 0) {
      out.push({ key: 'calendar', label: 'Group calendar updates', count: unreadCalendarCount, classes: 'bg-[#f1e8d2] border-[#d8c59a] text-[#5a3a1a]' });
    }
    if (isSalesItem(item) && unreadSalesCount > 0) {
      out.push({ key: 'sales', label: 'Sales updates', count: unreadSalesCount, classes: 'bg-[#f1e8d2] border-[#d8c59a] text-[#5a3a1a]' });
    }
    if (item.id === 'photo-log' && photoLogUneditedCount > 0) {
      out.push({ key: 'photo-log', label: 'Photo Log items needing edits', count: photoLogUneditedCount, classes: 'bg-[#f1e8d2] border-[#d8c59a] text-[#5a3a1a]' });
    }
    if (item.id === 'content-creation' && contentCreationSubmittedCount > 0) {
      out.push({ key: 'content-creation', label: 'Submitted content awaiting review', count: contentCreationSubmittedCount, classes: 'bg-[#f1e8d2] border-[#d8c59a] text-[#5a3a1a]' });
    }
    if (item.id === 'jewelry-requests' && jewelryRequestsPendingCount > 0) {
      out.push({ key: 'jewelry-requests', label: 'Pending jewelry requests', count: jewelryRequestsPendingCount, classes: 'bg-[#f1e8d2] border-[#d8c59a] text-[#5a3a1a]' });
    }
    if (isCrmItem(item)) {
      if (crmFollowUpCount > 0) {
        out.push({ key: 'crm-followup', label: 'CRM follow-ups', count: crmFollowUpCount, classes: 'bg-[#f1e8d2] border-[#d8c59a] text-[#5a3a1a]' });
      }
      if (unreadLiveChatSidebarCount > 0) {
        out.push({ key: 'crm-livechat', label: 'Unread live-chat messages', count: unreadLiveChatSidebarCount, classes: 'bg-[#f1e8d2] border-[#d8c59a] text-[#5a3a1a]' });
      }
    }
    if (isCompanyRatingsItem(item) && newSurveyResponseCount > 0) {
      out.push({ key: 'company-ratings', label: 'New survey responses', count: newSurveyResponseCount, classes: 'bg-[#f1e8d2] border-[#d8c59a] text-[#5a3a1a]' });
    }
    if (isInventoryDatabaseItem(item) && inventoryDecisionsCount > 0) {
      out.push({ key: 'inventory-database', label: 'Unresolved inventory decisions', count: inventoryDecisionsCount, classes: 'bg-[#f1e8d2] border-[#d8c59a] text-[#5a3a1a]' });
    }
    if (isStoreEmailItem(item) && unreadStoreEmailCount > 0) {
      out.push({ key: 'store-email', label: 'Unread store emails', count: unreadStoreEmailCount, classes: 'bg-[#f1e8d2] border-[#d8c59a] text-[#5a3a1a]' });
    }
    return out;
  };

  // Walk a folder's descendants and collect every contributing badge.
  // We keep them per-source so the UI renders separate pills rather than
  // summing them into one ambiguous number.
  const folderBadgeBreakdown = (folder: SidebarItem): SidebarBadge[] => {
    if (folder.type !== 'folder') return [];
    const result: SidebarBadge[] = [];
    for (const child of folder.children || []) {
      if (child.type === 'folder') {
        result.push(...folderBadgeBreakdown(child));
      } else {
        result.push(...itemBadges(child));
      }
    }
    return result;
  };

  // Sidebar search: filter the tree down to items whose name (or path) match
  // the query. Folders are kept whenever they OR any descendant matches.
  // Returns both the trimmed tree and a Set of folder ids to force-expand
  // so matching descendants are visible without the user clicking through.
  const filteredSidebarView = useMemo<{
    tree: SidebarItem[];
    expandIds: Set<string>;
    isSearching: boolean;
  }>(() => {
    const q = sidebarSearchQuery.trim().toLowerCase();
    if (!q) {
      return { tree: sidebarConfig, expandIds: new Set<string>(), isSearching: false };
    }
    const expandIds = new Set<string>();
    const matches = (item: SidebarItem): boolean => {
      if (item.name && item.name.toLowerCase().includes(q)) return true;
      if (item.type === 'link' && item.path && item.path.toLowerCase().includes(q)) return true;
      return false;
    };
    const recurse = (nodes: SidebarItem[]): SidebarItem[] => {
      const out: SidebarItem[] = [];
      for (const node of nodes) {
        if (node.type === 'folder') {
          const filteredChildren = recurse(node.children || []);
          if (matches(node) || filteredChildren.length > 0) {
            if (filteredChildren.length > 0) expandIds.add(node.id);
            out.push({ ...node, children: filteredChildren });
          }
        } else if (matches(node)) {
          out.push(node);
        }
      }
      return out;
    };
    return { tree: recurse(sidebarConfig), expandIds, isSearching: true };
  }, [sidebarConfig, sidebarSearchQuery]);

  // While a search is active, force every folder containing a match open
  // (without mutating the user's manual openFolders state). Once they clear
  // the search, their original expand/collapse state is restored.
  const isFolderOpen = (id: string): boolean =>
    openFolders[id] === true || filteredSidebarView.expandIds.has(id);

  // Store Staff / Store Manager get a flattened sidebar: instead of the
  // file-tree with collapsible folders, every accessible link is surfaced as
  // a normal top-level menu link. Folders the user can't access are dropped
  // entirely (preserving the same visibility as the foldered layout).
  // Admins and templaters who also work a store shift keep the foldered layout;
  // Store View is how they see the store menu.
  const hasOfficeRole =
    currentUser?.roles?.some((role) => String(role).endsWith('Admin') || String(role).startsWith('Template')) ?? false;
  const useFlatLayout =
    storeViewPreview ||
    (!hasOfficeRole &&
      (currentUser?.roles?.some(
        (role) => role === UserRole.StoreStaff || role === UserRole.StoreManager
      ) ?? false));

  const flattenTree = (items: SidebarItem[]): SidebarItem[] => {
    const out: SidebarItem[] = [];
    for (const item of items) {
      if (!hasItemAccess(item)) continue;
      if (item.type === 'folder') {
        out.push(...flattenTree(item.children || []));
      } else {
        out.push(item);
      }
    }
    return out;
  };

  const storeViewRows = useMemo(() => {
    const accessible = flattenTree(filteredSidebarView.tree);
    const byId = new Map(accessible.map((item) => [item.id, item]));
    const used = new Set<string>();
    type Row = { kind: 'header'; id: string; name: string } | { kind: 'link'; item: SidebarItem };
    const rows: Row[] = [];

    for (const entry of storeViewEntries) {
      if (entry.type === 'header') {
        rows.push({ kind: 'header', id: entry.id, name: entry.name });
        continue;
      }
      const item = byId.get(entry.id);
      if (item) {
        rows.push({ kind: 'link', item });
        used.add(entry.id);
      }
    }
    for (const item of accessible) {
      if (!used.has(item.id)) rows.push({ kind: 'link', item });
    }

    return rows.filter((row, i) => {
      if (row.kind !== 'header') return true;
      for (let j = i + 1; j < rows.length; j++) {
        if (rows[j].kind === 'header') return false;
        if (rows[j].kind === 'link') return true;
      }
      return false;
    });
  }, [storeViewEntries, filteredSidebarView.tree, currentUser?.roles, storeViewPreview]);

  const displayTree = useFlatLayout
    ? storeViewRows.filter((row): row is { kind: 'link'; item: SidebarItem } => row.kind === 'link').map((row) => row.item)
    : filteredSidebarView.tree;

  const showsCrmLink = (items: SidebarItem[]): boolean =>
    items.some((item) =>
      item.type === 'folder'
        ? hasItemAccess(item) && showsCrmLink(item.children || [])
        : isCrmItem(item) && hasItemAccess(item) && (storeViewPreview || !isPathExcluded(item.path, currentUser?.excludedPages))
    );
  const crmFollowUpCount = useCrmFollowUpCount(showsCrmLink(filteredSidebarView.tree));
  const hasCrmBadges = crmFollowUpCount > 0 || unreadLiveChatSidebarCount > 0;

  const renderSidebarItem = (item: SidebarItem, level = 0) => {
    if (!hasItemAccess(item)) return null;
    // Per-user page exclusion overrides role access (incl. full-access roles).
    // Skip this in Store View preview: the admin's personal exclusions aren't
    // relevant when previewing the generic Store Manager layout.
    if (item.type === 'link' && !storeViewPreview && isPathExcluded(item.path, currentUser?.excludedPages)) return null;
    const paddingLeft = level * 1.5 + 1;

    const getTextColorClass = (color?: string) => {
      if (color === 'custom') return '';
      switch (color) {
        // Original colors
        case 'red': return 'text-red-400 hover:text-red-300';
        case 'yellow': return 'text-yellow-400 hover:text-yellow-300';
        case 'orange': return 'text-orange-400 hover:text-orange-300';
        case 'green': return 'text-emerald-400 hover:text-emerald-300';
        case 'blue': return 'text-blue-400 hover:text-blue-300';
        case 'purple': return 'text-purple-400 hover:text-purple-300';
        case 'pink': return 'text-pink-400 hover:text-pink-300';
        case 'teal': return 'text-teal-400 hover:text-teal-300';
        case 'indigo': return 'text-indigo-400 hover:text-indigo-300';
        case 'rose': return 'text-rose-400 hover:text-rose-300';
        case 'slate': return 'text-slate-400 hover:text-slate-300';
        case 'cyan': return 'text-cyan-400 hover:text-cyan-300';
        case 'lime': return 'text-lime-400 hover:text-lime-300';
        case 'amber': return 'text-amber-400 hover:text-amber-300';
        case 'emerald': return 'text-emerald-400 hover:text-emerald-300';
        case 'violet': return 'text-violet-400 hover:text-violet-300';
        case 'fuchsia': return 'text-fuchsia-400 hover:text-fuchsia-300';
        case 'sky': return 'text-sky-400 hover:text-sky-300';
        case 'gray': return 'text-gray-400 hover:text-gray-300';
        case 'stone': return 'text-stone-400 hover:text-stone-300';
        case 'zinc': return 'text-zinc-400 hover:text-zinc-300';
        case 'neutral': return 'text-neutral-400 hover:text-neutral-300';
        case 'black': return 'text-gray-800 hover:text-gray-700';
        case 'crimson': return 'text-red-600 hover:text-red-500';
        case 'gold': return 'text-yellow-500 hover:text-yellow-400';
        case 'silver': return 'text-slate-300 hover:text-slate-200';
        case 'bronze': return 'text-amber-600 hover:text-amber-500';
        case 'mint': return 'text-green-300 hover:text-green-200';
        case 'coral': return 'text-orange-300 hover:text-orange-200';
        
        // NEW Solid Colors - Gemstone & Metal Theme
        case 'ruby': return 'text-red-500 hover:text-red-400';
        case 'sapphire': return 'text-blue-600 hover:text-blue-500';
        case 'topaz': return 'text-yellow-600 hover:text-yellow-500';
        case 'onyx': return 'text-gray-900 hover:text-gray-800';
        case 'pearl': return 'text-gray-100 hover:text-white';
        case 'platinum': return 'text-gray-200 hover:text-gray-100';
        case 'copper': return 'text-orange-600 hover:text-orange-500';
        case 'steel': return 'text-gray-500 hover:text-gray-400';
        case 'iron': return 'text-gray-600 hover:text-gray-500';
        case 'titanium': return 'text-gray-300 hover:text-gray-200';
        
        // Special Effect Colors
        case 'neon': return 'text-green-400 hover:text-green-300 drop-shadow-[0_0_8px_rgba(34,197,94,0.8)]';
        case 'electric': return 'text-blue-400 hover:text-blue-300 drop-shadow-[0_0_8px_rgba(59,130,246,0.8)]';
        case 'glow': return 'text-yellow-300 hover:text-yellow-200 drop-shadow-[0_0_8px_rgba(253,224,71,0.8)]';
        case 'flame': return 'text-red-500 hover:text-red-400 drop-shadow-[0_0_8px_rgba(239,68,68,0.8)]';
        case 'ice': return 'text-cyan-300 hover:text-cyan-200 drop-shadow-[0_0_8px_rgba(103,232,249,0.8)]';
        case 'storm': return 'text-indigo-400 hover:text-indigo-300 drop-shadow-[0_0_8px_rgba(129,140,248,0.8)]';
        case 'sunset': return 'text-orange-400 hover:text-orange-300';
        case 'dawn': return 'text-pink-300 hover:text-pink-200';
        case 'midnight': return 'text-indigo-900 hover:text-indigo-800';
        case 'rainbow': return 'text-transparent bg-gradient-to-r from-red-400 via-yellow-400 via-green-400 via-blue-400 via-indigo-400 to-purple-400 bg-clip-text hover:from-red-300 hover:via-yellow-300 hover:via-green-300 hover:via-blue-300 hover:via-indigo-300 hover:to-purple-300';
        case 'aurora': return 'text-transparent bg-gradient-to-r from-green-400 via-blue-500 to-purple-600 bg-clip-text hover:from-green-300 hover:via-blue-400 hover:to-purple-500';
        
        // GRADIENT Colors - These will be amazing!
        case 'gradient-fire': return 'text-transparent bg-gradient-to-r from-red-500 via-orange-500 to-yellow-500 bg-clip-text hover:from-red-400 hover:via-orange-400 hover:to-yellow-400';
        case 'gradient-ocean': return 'text-transparent bg-gradient-to-r from-blue-600 via-cyan-500 to-teal-400 bg-clip-text hover:from-blue-500 hover:via-cyan-400 hover:to-teal-300';
        case 'gradient-sunset': return 'text-transparent bg-gradient-to-r from-purple-600 via-pink-500 to-orange-400 bg-clip-text hover:from-purple-500 hover:via-pink-400 hover:to-orange-300';
        case 'gradient-aurora': return 'text-transparent bg-gradient-to-r from-green-400 via-blue-500 via-purple-600 to-pink-500 bg-clip-text hover:from-green-300 hover:via-blue-400 hover:via-purple-500 hover:to-pink-400';
        case 'gradient-cosmic': return 'text-transparent bg-gradient-to-r from-indigo-900 via-purple-800 to-pink-700 bg-clip-text hover:from-indigo-800 hover:via-purple-700 hover:to-pink-600';
        case 'gradient-forest': return 'text-transparent bg-gradient-to-r from-green-800 via-emerald-600 to-lime-500 bg-clip-text hover:from-green-700 hover:via-emerald-500 hover:to-lime-400';
        case 'gradient-royal': return 'text-transparent bg-gradient-to-r from-purple-800 via-blue-700 to-indigo-600 bg-clip-text hover:from-purple-700 hover:via-blue-600 hover:to-indigo-500';
        case 'gradient-neon': return 'text-transparent bg-gradient-to-r from-pink-500 via-green-400 to-cyan-400 bg-clip-text hover:from-pink-400 hover:via-green-300 hover:to-cyan-300 drop-shadow-[0_0_8px_rgba(236,72,153,0.6)]';
        case 'gradient-ice': return 'text-transparent bg-gradient-to-r from-blue-200 via-cyan-300 to-blue-100 bg-clip-text hover:from-blue-100 hover:via-cyan-200 hover:to-blue-50';
        case 'gradient-volcano': return 'text-transparent bg-gradient-to-r from-red-700 via-orange-600 to-yellow-500 bg-clip-text hover:from-red-600 hover:via-orange-500 hover:to-yellow-400';
        case 'gradient-galaxy': return 'text-transparent bg-gradient-to-r from-indigo-600 via-purple-700 via-pink-600 to-red-500 bg-clip-text hover:from-indigo-500 hover:via-purple-600 hover:via-pink-500 hover:to-red-400';
        case 'gradient-rainbow': return 'text-transparent bg-gradient-to-r from-red-500 via-yellow-500 via-green-500 via-blue-500 via-indigo-500 to-purple-500 bg-clip-text hover:from-red-400 hover:via-yellow-400 hover:via-green-400 hover:via-blue-400 hover:via-indigo-400 hover:to-purple-400';
        
        default: return 'text-gray-300 hover:text-white';
      }
    };

    if (item.type === 'folder') {
      const customColorStyle = item.textColor === 'custom' && item.customTextColor ? { color: item.customTextColor } : undefined;
      return (
        <div key={item.id} className="relative">
          <button
            onClick={() => toggleFolder(item.id)}
            className={cn(
              'w-full flex items-center px-4 py-3 hover:bg-[#241813] transition-all duration-300 relative',
              mentionsClaire(item) && 'isolate overflow-hidden',
              getTextColorClass(item.textColor),
              isActive(item.path) && 'bg-[#2b1d12] border-l-[3px] border-[#f1e8d2] text-white'
            )}
            style={{ paddingLeft: `${paddingLeft}rem`, ...customColorStyle }}
          >
            {mentionsClaire(item) && <ClaireBackground />}
            {renderIcon(item.icon, item.textColor, item.customTextColor)}
            {effectiveExpanded && (() => {
              // Only roll-up notifications onto the folder when it's
              // collapsed; once the user expands it, every descendant
              // shows its own badge so we'd be double-counting.
              const badges = !isFolderOpen(item.id) ? folderBadgeBreakdown(item) : [];
              const hasBadges = badges.length > 0;
              return (
                <>
                  <span
                    className="ml-3"
                    style={{
                      fontFamily: sidebarFontConfig.fontFamily,
                      fontSize: `${sidebarFontConfig.fontSize}px`,
                      fontWeight: sidebarFontConfig.fontWeight,
                      fontStyle: sidebarFontConfig.fontStyle,
                      lineHeight: sidebarFontConfig.lineHeight,
                      letterSpacing: `${sidebarFontConfig.letterSpacing}px`
                    }}
                  >
                    {item.name}
                  </span>
                  {hasBadges && (
                    <span className="ml-auto flex items-center gap-1">
                      {badges.map(b => (
                        <span
                          key={b.key}
                          className={cn(
                            'border text-xs font-bold rounded-full h-5 flex items-center justify-center min-w-[20px] px-2 shadow-sm',
                            b.classes,
                          )}
                          title={`${b.count} ${b.label}`}
                        >
                          {b.count > 99 ? '99+' : b.count}
                        </span>
                      ))}
                    </span>
                  )}
                  <Icons.ChevronRight
                    size={16}
                    strokeWidth={1.25}
                    className={cn(
                      'transition-transform',
                      hasBadges ? 'ml-1.5' : 'ml-auto',
                      isFolderOpen(item.id) && 'rotate-90'
                    )}
                  />
                </>
              );
            })()}
          </button>
          {/* Separator line with fade effect */}
          <div className="relative">
            <div className={cn(
              'absolute inset-x-4 h-px bg-gradient-to-r from-transparent to-transparent',
              'via-[#262626]'
            )}></div>
          </div>
          <AnimatePresence>
            {isFolderOpen(item.id) && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                {(() => {
                  // File-tree connector: a small curved L drawn on the left
                  // of each child + a vertical stub continuing to the next
                  // sibling. The vertical line stops on the last child so the
                  // structure visually closes off (like a file explorer).
                  const visibleChildren = (item.children || []).filter((c) => hasItemAccess(c));
                  const lineColor = '#4a3623';
                  const lineWidth = 2;
                  // Anchor the connector roughly at the parent icon's center
                  // so children look like they branch out from the folder.
                  const connectorLeft = `${paddingLeft + 0.5}rem`;
                  const elbowHeight = 22; // px — reaches the child row's vertical center
                  return visibleChildren.map((child, idx) => {
                    const isLast = idx === visibleChildren.length - 1;
                    return (
                      <div key={child.id} className="relative">
                        {/* L-shape: vertical drop + curve out toward the child */}
                        <div
                          aria-hidden
                          className="pointer-events-none absolute"
                          style={{
                            left: connectorLeft,
                            top: 0,
                            width: '0.7rem',
                            height: `${elbowHeight}px`,
                            borderLeft: `${lineWidth}px solid ${lineColor}`,
                            borderBottom: `${lineWidth}px solid ${lineColor}`,
                            borderBottomLeftRadius: '10px',
                          }}
                        />
                        {/* Vertical line continuing on to the next sibling.
                            Omitted for the last child so the trunk closes. */}
                        {!isLast && (
                          <div
                            aria-hidden
                            className="pointer-events-none absolute"
                            style={{
                              left: connectorLeft,
                              top: `${elbowHeight}px`,
                              bottom: 0,
                              borderLeft: `${lineWidth}px solid ${lineColor}`,
                            }}
                          />
                        )}
                        {renderSidebarItem(child, level + 1)}
                      </div>
                    );
                  });
                })()}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      );
    }

    const linkCustomStyle = item.textColor === 'custom' && item.customTextColor ? { color: item.customTextColor } : undefined;
    return (
      <div key={item.id} className="relative">
        <NavLink
          to={item.path || '#'}
          onClick={() => {
            if (isMobile && onClose) {
              onClose();
            }
          }}
          className={({ isActive }) =>
            cn(
              'flex items-center px-4 py-3 hover:bg-[#241813] transition-all duration-300 relative',
              mentionsClaire(item) && 'isolate overflow-hidden',
              getTextColorClass(item.textColor),
              isActive && 'bg-[#2b1d12] border-l-[3px] border-[#f1e8d2] text-white'
            )
          }
          style={{ paddingLeft: `${paddingLeft}rem`, ...linkCustomStyle }}
        >
          {mentionsClaire(item) && <ClaireBackground />}
          {/* Swap icon to animated icon when there are unread notifications */}
          {isChatItem(item) && unreadChatsCount > 0 ? (
            <span className="relative">
              <Icons.MessageSquarePlus size={20} strokeWidth={1.25} className="text-white" />
              <span className="absolute -top-1.5 -right-1.5 h-2.5 w-2.5 rounded-full bg-[#f1e8d2]" />
            </span>
          ) : isCalendarItem(item) && unreadCalendarCount > 0 ? (
            <span className="relative">
              <Icons.CalendarPlus size={20} strokeWidth={1.25} className="text-white" />
              <span className="absolute -top-1.5 -right-1.5 h-2.5 w-2.5 rounded-full bg-[#f1e8d2]" />
            </span>
          ) : isSalesItem(item) && unreadSalesCount > 0 ? (
            <span className="relative">
              <Icons.DollarSign size={20} strokeWidth={1.25} className="text-white" />
              <span className="absolute -top-1.5 -right-1.5 h-2.5 w-2.5 rounded-full bg-[#f1e8d2]" />
            </span>
          ) : item.id === 'photo-log' && photoLogUneditedCount > 0 ? (
            <span className="relative">
              <Icons.Camera size={20} strokeWidth={1.25} className="text-white" />
              <span className="absolute -top-1.5 -right-1.5 h-2.5 w-2.5 rounded-full bg-[#f1e8d2]" />
            </span>
          ) : item.id === 'jewelry-requests' && jewelryRequestsPendingCount > 0 ? (
            <span className="relative">
              <Icons.Gem size={20} strokeWidth={1.25} className="text-white" />
              <span className="absolute -top-1.5 -right-1.5 h-2.5 w-2.5 rounded-full bg-[#f1e8d2]" />
            </span>
          ) : isCrmItem(item) && hasCrmBadges ? (
            <span className="relative">
              <Icons.MessageSquare size={20} strokeWidth={1.25} className="text-white" />
              <span className="absolute -top-1.5 -right-1.5 h-2.5 w-2.5 rounded-full bg-[#f1e8d2]" />
            </span>
          ) : isCompanyRatingsItem(item) && newSurveyResponseCount > 0 ? (
            <span className="relative">
              <Icons.BarChart size={20} strokeWidth={1.25} className="text-white" />
              <span className="absolute -top-1.5 -right-1.5 h-2.5 w-2.5 rounded-full bg-[#f1e8d2]" />
            </span>
          ) : (
            renderIcon(item.icon, item.textColor, item.customTextColor)
          )}
          {effectiveExpanded && (
            <>
              <span 
                className="ml-3"
                style={{
                  fontFamily: sidebarFontConfig.fontFamily,
                  fontSize: `${sidebarFontConfig.fontSize}px`,
                  fontWeight: sidebarFontConfig.fontWeight,
                  fontStyle: sidebarFontConfig.fontStyle,
                  lineHeight: sidebarFontConfig.lineHeight,
                  letterSpacing: `${sidebarFontConfig.letterSpacing}px`
                }}
              >
                {item.name}
              </span>
              {item.id === 'template-admin' && templateReviewCount > 0 && (
                <span className="ml-auto bg-[#f1e8d2] border border-[#d8c59a] text-[#5a3a1a] text-xs font-bold rounded-full h-5 flex items-center justify-center min-w-[20px] px-2 shadow-sm">
                  {templateReviewCount}
                </span>
              )}
              {item.id === 'template' && templateNeedsCorrectionsCount > 0 && (
                <span className="ml-auto bg-[#f1e8d2] border border-[#d8c59a] text-[#5a3a1a] text-xs font-bold rounded-full h-5 flex items-center justify-center min-w-[20px] px-2 shadow-sm">
                  {templateNeedsCorrectionsCount}
                </span>
              )}
              {isChatItem(item) && unreadChatsCount > 0 && (
                <span className="ml-auto bg-[#f1e8d2] border border-[#d8c59a] text-[#5a3a1a] text-xs font-bold rounded-full h-5 flex items-center justify-center min-w-[20px] px-2 shadow-sm">
                  {unreadChatsCount}
                </span>
              )}
              {isCalendarItem(item) && unreadCalendarCount > 0 && (
                <span className="ml-auto bg-[#f1e8d2] border border-[#d8c59a] text-[#5a3a1a] text-xs font-bold rounded-full h-5 flex items-center justify-center min-w-[20px] px-2 shadow-sm">
                  {unreadCalendarCount}
                </span>
              )}
              {isSalesItem(item) && unreadSalesCount > 0 && (
                <span className="ml-auto bg-[#f1e8d2] border border-[#d8c59a] text-[#5a3a1a] text-xs font-bold rounded-full h-5 flex items-center justify-center min-w-[20px] px-2 shadow-sm">
                  {unreadSalesCount}
                </span>
              )}
              {item.id === 'photo-log' && photoLogUneditedCount > 0 && (
                <span className="ml-auto bg-[#f1e8d2] border border-[#d8c59a] text-[#5a3a1a] text-xs font-bold rounded-full h-5 flex items-center justify-center min-w-[20px] px-2 shadow-sm">
                  {photoLogUneditedCount}
                </span>
              )}
              {item.id === 'content-creation' && contentCreationSubmittedCount > 0 && (
                <span className="ml-auto bg-[#f1e8d2] border border-[#d8c59a] text-[#5a3a1a] text-xs font-bold rounded-full h-5 flex items-center justify-center min-w-[20px] px-2 shadow-sm">
                  {contentCreationSubmittedCount}
                </span>
              )}
              {item.id === 'jewelry-requests' && jewelryRequestsPendingCount > 0 && (
                <span className="ml-auto bg-[#f1e8d2] border border-[#d8c59a] text-[#5a3a1a] text-xs font-bold rounded-full h-5 flex items-center justify-center min-w-[20px] px-2 shadow-sm">
                  {jewelryRequestsPendingCount}
                </span>
              )}
              {isCrmItem(item) && hasCrmBadges && (
                <span className="ml-auto flex items-center gap-1">
                  {crmFollowUpCount > 0 && (
                    <span className="bg-[#f1e8d2] border border-[#d8c59a] text-[#5a3a1a] text-xs font-bold rounded-full h-5 flex items-center justify-center min-w-[20px] px-2 shadow-sm">
                      {crmFollowUpCount}
                    </span>
                  )}
                  {unreadLiveChatSidebarCount > 0 && (
                    <span className="bg-[#f1e8d2] border border-[#d8c59a] text-[#5a3a1a] text-xs font-bold rounded-full h-5 flex items-center justify-center min-w-[20px] px-2 shadow-sm">
                      {unreadLiveChatSidebarCount}
                    </span>
                  )}
                </span>
              )}
              {isCompanyRatingsItem(item) && newSurveyResponseCount > 0 && (
                <span className="ml-auto bg-[#f1e8d2] border border-[#d8c59a] text-[#5a3a1a] text-xs font-bold rounded-full h-5 flex items-center justify-center min-w-[20px] px-2 shadow-sm">
                  {newSurveyResponseCount}
                </span>
              )}
              {isInventoryDatabaseItem(item) && inventoryDecisionsCount > 0 && (
                <span
                  className="ml-auto bg-[#f1e8d2] border border-[#d8c59a] text-[#5a3a1a] text-xs font-bold rounded-full h-5 flex items-center justify-center min-w-[20px] px-2 shadow-sm"
                  title={`${inventoryDecisionsCount} unresolved inventory decision${inventoryDecisionsCount !== 1 ? 's' : ''}`}
                >
                  {inventoryDecisionsCount > 99 ? '99+' : inventoryDecisionsCount}
                </span>
              )}
              {isStoreEmailItem(item) && unreadStoreEmailCount > 0 && (
                <span className="ml-auto bg-[#f1e8d2] border border-[#d8c59a] text-[#5a3a1a] text-xs font-bold rounded-full h-5 flex items-center justify-center min-w-[20px] px-2 shadow-sm">
                  {unreadStoreEmailCount > 99 ? '99+' : unreadStoreEmailCount}
                </span>
              )}
            </>
          )}
          {!effectiveExpanded && item.id === 'template-admin' && templateReviewCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-[#f1e8d2] border border-[#d8c59a] text-[#5a3a1a] text-xs font-bold rounded-full h-4 flex items-center justify-center min-w-[16px] px-1 shadow-sm">
              {templateReviewCount > 99 ? '99+' : templateReviewCount}
            </span>
          )}
          {!effectiveExpanded && item.id === 'template' && templateNeedsCorrectionsCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-[#f1e8d2] border border-[#d8c59a] text-[#5a3a1a] text-xs font-bold rounded-full h-4 flex items-center justify-center min-w-[16px] px-1 shadow-sm">
              {templateNeedsCorrectionsCount > 99 ? '99+' : templateNeedsCorrectionsCount}
            </span>
          )}
          {!effectiveExpanded && isChatItem(item) && unreadChatsCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-[#f1e8d2] border border-[#d8c59a] text-[#5a3a1a] text-xs font-bold rounded-full h-4 flex items-center justify-center min-w-[16px] px-1 shadow-sm">
              {unreadChatsCount > 99 ? '99+' : unreadChatsCount}
            </span>
          )}
          {!effectiveExpanded && isCalendarItem(item) && unreadCalendarCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-[#f1e8d2] border border-[#d8c59a] text-[#5a3a1a] text-xs font-bold rounded-full h-4 flex items-center justify-center min-w-[16px] px-1 shadow-sm">
              {unreadCalendarCount > 99 ? '99+' : unreadCalendarCount}
            </span>
          )}
          {!effectiveExpanded && isSalesItem(item) && unreadSalesCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-[#f1e8d2] border border-[#d8c59a] text-[#5a3a1a] text-xs font-bold rounded-full h-4 flex items-center justify-center min-w-[16px] px-1 shadow-sm">
              {unreadSalesCount > 99 ? '99+' : unreadSalesCount}
            </span>
          )}
          {!effectiveExpanded && item.id === 'photo-log' && photoLogUneditedCount > 0 && (
            <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-[#f1e8d2]" />
          )}
          {!effectiveExpanded && item.id === 'content-creation' && contentCreationSubmittedCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-[#f1e8d2] border border-[#d8c59a] text-[#5a3a1a] text-xs font-bold rounded-full h-4 flex items-center justify-center min-w-[16px] px-1 shadow-sm">
              {contentCreationSubmittedCount > 99 ? '99+' : contentCreationSubmittedCount}
            </span>
          )}
          {!effectiveExpanded && item.id === 'jewelry-requests' && jewelryRequestsPendingCount > 0 && (
            <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-[#f1e8d2]" />
          )}
          {!effectiveExpanded && isCrmItem(item) && hasCrmBadges && (
            <span className="absolute -top-1 -right-1 flex gap-0.5">
              {crmFollowUpCount > 0 && (
                <span className="bg-[#f1e8d2] border border-[#d8c59a] text-[#5a3a1a] text-[9px] font-bold rounded-full h-4 flex items-center justify-center min-w-[16px] px-1 shadow-sm">
                  {crmFollowUpCount > 99 ? '99+' : crmFollowUpCount}
                </span>
              )}
              {unreadLiveChatSidebarCount > 0 && (
                <span className="bg-[#f1e8d2] border border-[#d8c59a] text-[#5a3a1a] text-[9px] font-bold rounded-full h-4 flex items-center justify-center min-w-[16px] px-1 shadow-sm">
                  {unreadLiveChatSidebarCount > 99 ? '99+' : unreadLiveChatSidebarCount}
                </span>
              )}
            </span>
          )}
          {!effectiveExpanded && isCompanyRatingsItem(item) && newSurveyResponseCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-[#f1e8d2] border border-[#d8c59a] text-[#5a3a1a] text-xs font-bold rounded-full h-4 flex items-center justify-center min-w-[16px] px-1 shadow-sm">
              {newSurveyResponseCount > 99 ? '99+' : newSurveyResponseCount}
            </span>
          )}
          {!effectiveExpanded && isInventoryDatabaseItem(item) && inventoryDecisionsCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-[#f1e8d2] border border-[#d8c59a] text-[#5a3a1a] text-xs font-bold rounded-full h-4 flex items-center justify-center min-w-[16px] px-1 shadow-sm">
              {inventoryDecisionsCount > 99 ? '99+' : inventoryDecisionsCount}
            </span>
          )}
          {!effectiveExpanded && isStoreEmailItem(item) && unreadStoreEmailCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-[#f1e8d2] border border-[#d8c59a] text-[#5a3a1a] text-xs font-bold rounded-full h-4 flex items-center justify-center min-w-[16px] px-1 shadow-sm">
              {unreadStoreEmailCount > 99 ? '99+' : unreadStoreEmailCount}
            </span>
          )}
        </NavLink>
        {/* Separator line with fade effect */}
        <div className="relative">
          <div className={cn(
            'absolute inset-x-4 h-px bg-gradient-to-r from-transparent to-transparent',
            'via-[#262626]'
          )}></div>
        </div>
      </div>
    );
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  return (
    <AnimatePresence>
      {(isMobileOpen || !isMobile) && (
        <>
          {isMobile && (
            <div className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm" onClick={onClose} />
          )}

          <motion.div
            initial={{ x: isMobile ? -300 : 0 }}
            animate={{ x: 0 }}
            exit={{ x: isMobile ? -300 : 0 }}
            transition={{ type: 'tween', duration: 0.3 }}
            className={cn(
              'fixed z-50 flex flex-col bg-[#1c130b]',
              isMobile
                ? 'w-[260px] left-0 top-0 h-screen shadow-sm rounded-none'
                : 'w-[256px] top-0 left-0 bottom-0'
            )}
          >
            <div className={cn(
              'flex flex-col h-full overflow-hidden'
            )}>
              <div className={cn(
                'flex-none px-4 py-3',
                'border-b border-[#2e2015]'
              )}>
                {!isMobile || expanded ? (
                  <div className={cn('flex justify-center px-2 py-2')}>
                    <img
                      src={ccoStoreCenterLogo}
                      alt="Chorost & Co. Store Center"
                      className="w-full max-h-14 object-contain"
                    />
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <img
                      src={ccoStoreCenterLogo}
                      alt="Chorost & Co. Store Center"
                      className="w-full max-h-10 object-contain"
                    />
                    <NotificationBell size="sm" />
                  </div>
                )}
                
              </div>

              {effectiveExpanded && (
                <div className={cn(
                  'flex-none p-4',
                  'border-b border-[#2e2015]'
                )}>
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
                      {userInfo.photoURL ? (
                        <img
                          src={userInfo.photoURL}
                          alt={userInfo.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                            if (nextElement) {
                              nextElement.style.display = 'flex';
                            }
                          }}
                        />
                      ) : null}
                      <div 
                        className={`w-full h-full bg-gradient-to-br from-emerald-400 via-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm ${
                          userInfo.photoURL ? 'hidden' : 'flex'
                        }`}
                      >
                        {userInfo.name ? userInfo.name.charAt(0).toUpperCase() : userInfo.email.charAt(0).toUpperCase()}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium truncate text-gray-100">{userInfo.name}</p>
                        <NotificationBell size="sm" />
                      </div>
                      <p className="text-xs truncate text-gray-500">{userInfo.email}</p>
                    </div>
                  </div>
                  <button
                    onClick={handleLogout}
                    className={cn(
                      'mt-3 flex items-center text-sm transition-colors w-full',
                      'text-gray-500 hover:text-white'
                    )}
                  >
                    <Icons.LogOut className="h-5 w-5 mr-2" strokeWidth={1.25} />
                    <span>Logout</span>
                  </button>
                </div>
              )}

              {/* In-sidebar page finder. Pinned above the scroll so it stays
                  visible while the list scrolls. Auto-expands folders that
                  contain matches; press Esc or click the X to clear. */}
              {effectiveExpanded && (
                <div
                  className={cn(
                    'flex-none px-3 pt-1 pb-2',
                    'border-b border-[#2e2015]'
                  )}
                >
                  <div className="flex items-center gap-1.5">
                    <div className="relative flex-1">
                      <Icons.Search
                        size={14}
                        strokeWidth={1.5}
                        className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500"
                      />
                      <input
                        type="text"
                        value={sidebarSearchQuery}
                        onChange={(e) => setSidebarSearchQuery(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Escape') {
                            setSidebarSearchQuery('');
                            (e.currentTarget as HTMLInputElement).blur();
                          }
                        }}
                        placeholder="Search pages…"
                        aria-label="Search sidebar pages"
                        className="w-full text-xs rounded-md pl-7 pr-7 py-1.5 transition-colors focus:outline-none bg-[#150e07] border border-[#2e2015] text-gray-200 placeholder:text-gray-600 focus:border-[#4a3623]"
                      />
                      {sidebarSearchQuery && (
                        <button
                          type="button"
                          onClick={() => setSidebarSearchQuery('')}
                          aria-label="Clear search"
                          className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded text-gray-500 hover:text-gray-200 hover:bg-[#2b1d12]"
                        >
                          <Icons.X size={12} strokeWidth={2} />
                        </button>
                      )}
                    </div>
                    {isMasterAdmin && !storeViewPreview && (
                      <button
                        type="button"
                        onClick={async () => {
                          if (!currentUser) return;
                          const ok = window.confirm(
                            'Broadcast a session refresh to ALL signed-in users?\n\nThey will see a 30-second countdown popup warning them to save their progress, then their browser will auto-reload.',
                          );
                          if (!ok) return;
                          try {
                            await triggerSessionRefresh(
                              currentUser.uid,
                              currentUser.displayName || 'Master Admin',
                              30,
                            );
                          } catch (err) {
                            console.error('Failed to broadcast session refresh', err);
                            window.alert('Failed to broadcast refresh — check your connection and try again.');
                          }
                        }}
                        title="Refresh all sessions"
                        aria-label="Refresh all sessions"
                        className="shrink-0 w-7 h-7 rounded-md flex items-center justify-center transition-colors text-gray-500 hover:text-amber-300 hover:bg-[#2e2015]"
                      >
                        <Icons.RefreshCw className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>

                  {/* Store View toggle — Master / Marketing admins can preview
                      the flattened Store Manager sidebar and switch back. */}
                  {canPreviewStoreView && (
                    <button
                      type="button"
                      onClick={() => {
                        setStoreViewActive((prev) => {
                          const next = !prev;
                          try {
                            localStorage.setItem('sidebar_store_view', next ? '1' : '0');
                          } catch {
                            /* ignore */
                          }
                          // Broadcast so other views (e.g. Inventory Database)
                          // can scope themselves to the CCO Store in real time.
                          try {
                            window.dispatchEvent(
                              new CustomEvent('store-view-changed', { detail: next })
                            );
                          } catch {
                            /* ignore */
                          }
                          return next;
                        });
                      }}
                      title={
                        storeViewActive
                          ? `Exit Store View — currently showing the ${storeViewPreviewRole} menu`
                          : `Store View — preview the ${storeViewPreviewRole} sidebar`
                      }
                      className={cn(
                        'mt-1.5 w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-md text-[11px] font-medium transition-colors border',
                        storeViewActive
                          ? 'bg-emerald-600/20 text-emerald-300 border-emerald-600/40'
                          : 'text-gray-400 hover:text-gray-200 hover:bg-[#2b1d12] border-[#2e2015]'
                      )}
                    >
                      <span className="flex items-center gap-1.5">
                        <Icons.Store size={13} strokeWidth={1.75} />
                        {storeViewActive ? 'Store View: On' : 'Store View'}
                      </span>
                      <span
                        className={cn(
                          'relative inline-flex h-4 w-7 shrink-0 items-center rounded-full transition-colors',
                          storeViewActive ? 'bg-emerald-500' : 'bg-gray-600'
                        )}
                      >
                        <span
                          className={cn(
                            'inline-block h-3 w-3 transform rounded-full bg-white transition-transform',
                            storeViewActive ? 'translate-x-3.5' : 'translate-x-0.5'
                          )}
                        />
                      </span>
                    </button>
                  )}
                </div>
              )}

              <div className="flex-1 overflow-y-auto custom-scrollbar">
                <div className="py-4">
                  {useFlatLayout ? (
                    storeViewRows.length > 0 ? (
                      storeViewRows.map((row, index) =>
                        row.kind === 'header' ? (
                          <div
                            key={row.id}
                            className={cn(
                              'px-4 pb-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#b8986e]',
                              index === 0 ? 'pt-1' : 'pt-4'
                            )}
                          >
                            {row.name}
                          </div>
                        ) : (
                          renderSidebarItem(row.item)
                        )
                      )
                    ) : (
                      filteredSidebarView.isSearching && (
                        <div className="px-4 py-6 text-center text-xs text-gray-500">
                          No pages match{' '}
                          <span className="text-gray-300">
                            "{sidebarSearchQuery}"
                          </span>
                        </div>
                      )
                    )
                  ) : displayTree.length > 0 ? (
                    displayTree.map((item) => renderSidebarItem(item))
                  ) : (
                    filteredSidebarView.isSearching && (
                      <div className="px-4 py-6 text-center text-xs text-gray-500">
                        No pages match{' '}
                        <span className="text-gray-300">
                          "{sidebarSearchQuery}"
                        </span>
                      </div>
                    )
                  )}
                </div>
              </div>

              {/* Watch Template - for Watch Admin */}
              {!storeViewPreview && currentUser?.roles?.some(role => [UserRole.WatchAdmin, UserRole.MasterAdmin, UserRole.CSAdmin].includes(role)) && (
                <div className="flex-none px-4 pb-2 border-t border-[#2e2015]">
                  <NavLink
                    to="/inventory-database?openWatch=true"
                    className="flex items-center gap-2 px-3 py-2 mt-3 rounded-md text-xs font-medium transition-colors text-gray-300 hover:text-white hover:bg-[#2e2015]"
                  >
                    <Icons.Watch className="w-4 h-4" />
                    {effectiveExpanded && <span>Watch Template</span>}
                  </NavLink>
                </div>
              )}


              {/* Active Users Section - pinned to bottom. Click someone to FIN chat them; click Claire to talk to her. */}
              {stripUsers.length > 0 && (
                <div className="flex-none px-4 pb-4 shadow-[0_-4px_8px_-4px_rgba(0,0,0,0.04)] border-t border-[#2e2015] bg-[#150e07]">
                    <div className="pt-4">
                      <h3 className="text-sm font-medium mb-3 text-gray-300">
                        Active Users ({activeUsers.length})
                      </h3>
                      <div className="grid grid-cols-5 gap-1.5">
                        {stripUsers.slice(0, 15).map((user) => {
                          const email = String(user.email || '').toLowerCase();
                          const unread = unreadFrom[email];
                          const isMe = email === myEmail;
                          const isClaire = isClaireEmail(email);
                          const action = isMe
                            ? ''
                            : isClaire && hasClaireAccess && !unread
                              ? 'Click to talk to Claire'
                              : `Click to message ${String(user.displayName).split(' ')[0]}`;
                          return (
                            <button
                              type="button"
                              key={user.userId}
                              onMouseEnter={(e) => setUserTip({ id: user.userId, el: e.currentTarget })}
                              onMouseLeave={() => setUserTip(null)}
                              onFocus={(e) => setUserTip({ id: user.userId, el: e.currentTarget })}
                              onBlur={() => setUserTip(null)}
                              onClick={() => openUserChat(user)}
                              disabled={isMe}
                              className={cn(
                                "group relative flex flex-col items-center rounded-full focus:outline-none",
                                isMe ? "cursor-default" : "cursor-pointer"
                              )}
                              aria-label={action || user.displayName}
                            >
                              <div className="relative">
                                <div className={cn(
                                  "w-8 h-8 rounded-full overflow-hidden bg-gray-200 ring-2 transition-all duration-200",
                                  unread ? "ring-red-500" : "ring-gray-800 group-hover:ring-[#c9a46a]",
                                  user.offline && !unread && "opacity-50 grayscale"
                                )}>
                                  {user.photoURL ? (
                                    <img
                                      src={user.photoURL}
                                      alt={user.displayName}
                                      className="w-full h-full object-cover"
                                      onError={(e) => {
                                        e.currentTarget.style.display = 'none';
                                        const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                                        if (nextElement) {
                                          nextElement.style.display = 'flex';
                                        }
                                      }}
                                    />
                                  ) : null}
                                  <div
                                    className={`w-full h-full bg-gradient-to-br from-emerald-400 via-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-[11px] ${
                                      user.photoURL ? 'hidden' : 'flex'
                                    }`}
                                  >
                                    {user.displayName ? user.displayName.charAt(0).toUpperCase() : email.charAt(0).toUpperCase()}
                                  </div>
                                </div>
                                {unread && (
                                  <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 ring-2 ring-[#150e07]">
                                    <Icons.MessageCircle className="h-2.5 w-2.5 text-white" strokeWidth={3} />
                                  </span>
                                )}
                              </div>
                              {/* Hover popup — Master Admin also sees the page they're active on. */}
                              <FloatingTip
                                anchor={userTip && userTip.id === user.userId ? userTip.el : null}
                                className="max-w-[260px] bg-gray-900 text-white text-xs px-2 py-1 rounded shadow-lg text-left"
                              >
                                <div className="font-medium whitespace-nowrap">{user.displayName}{isMe ? ' (you)' : ''}</div>
                                {isMasterAdmin && !user.offline && (
                                  <div className="text-[10px] text-gray-300 mt-0.5 whitespace-nowrap">
                                    {formatActivePagePath(user.currentPage)}
                                    {user.activity && <div className="text-amber-200">{user.activity}</div>}
                                  </div>
                                )}
                                {unread && (
                                  <div className="text-[10px] text-red-200 mt-0.5 line-clamp-2 min-w-[140px]">
                                    New message: {unread.preview || 'Attachment'}
                                  </div>
                                )}
                                {action && <div className="text-[10px] text-gray-400 mt-0.5 whitespace-nowrap">{action}</div>}
                              </FloatingTip>
                            </button>
                          );
                        })}
                      </div>
                      {stripUsers.length > 15 && (
                        <div className="text-xs text-gray-500 mt-2 text-center">
                          +{stripUsers.length - 15} more active users
                        </div>
                      )}
                    </div>
                  </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
